import * as THREE from 'three';
import {
  chamferBox,
  plainBox,
  tubeZ,
  rodZ,
  pipeZ,
  ringZ,
  picatinnyRail,
  slottedPanel,
  gripTexture,
  mergeGeom,
  place,
  translate,
  rotate,
  triCount,
  forgeMaterial,
} from './primitives.js';
import { buildHand, attachHands } from './hands.js';
import { buildFirearm } from './chassis.js';
import { SPECS } from './specs.js';

/**
 * Procedural weapon meshes.
 *
 * Model space, shared by every weapon:
 *   origin  = a point on the bore axis directly above the trigger
 *   -Z      = downrange (the muzzle)
 *   +X      = the shooter's right
 *   +Y      = up
 *   units   = real metres, so a rifle really is 0.84 m long. The viewmodel scales
 *             the whole thing down; keeping the source geometry life-sized means
 *             chamfer widths, rail pitch and magazine thickness can all be authored
 *             against real values instead of guessed ones.
 *
 * Each builder returns the mesh plus the three attachment points the animation and
 * ballistics layers need: `aimPoint` (the optical centre of the sight, used to solve
 * the ADS transform exactly rather than by eye), `muzzlePoint`, and `ejectPoint`.
 */

/* -------------------------------------------------------------------------- */
/* small local helpers                                                         */
/* -------------------------------------------------------------------------- */

/** Flat disc facing +Z (dir = 1) or -Z (dir = -1). */
function discZ(r, z, seg = 14, dir = 1) {
  return dir > 0 ? tubeZ([[r, z], [0, z]], seg) : tubeZ([[0, z], [r, z]], seg);
}

/**
 * Radial alpha ramp: transparent in the middle, opaque at the rim.
 *
 * This is the scope shadow — the ring of black that creeps in from the edge of a
 * magnified optic's ocular. It is the single strongest cue that a sight magnifies,
 * because it is the one thing a red dot never has. Generated rather than downloaded,
 * like everything else in this project.
 */
let _vignetteTex = null;
// `inner` is set so the ramp begins just *outside* the tube's own clear cone
// (measured at 3.6 degrees of 7.35): any tighter and the shadow eats the sight
// picture rather than softening the edge of it.
function vignetteTexture(size = 64, inner = 0.58, outer = 0.99) {
  if (_vignetteTex) return _vignetteTex;
  const data = new Uint8Array(size * size * 4);
  const c = (size - 1) / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const r = Math.hypot((x - c) / c, (y - c) / c);
      let a = (r - inner) / Math.max(1e-4, outer - inner);
      a = a <= 0 ? 0 : a >= 1 ? 1 : a * a * (3 - 2 * a);
      const i = (y * size + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = 0;
      data[i + 3] = Math.round(a * 255);
    }
  }
  const t = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearFilter;
  t.needsUpdate = true;
  _vignetteTex = t;
  return t;
}

/**
 * Matte-black sleeve just inside an optic's tube wall.
 *
 * Every sight in this file needs one. The shared gunmetal carries bright bare-steel
 * edge wear, so without a sleeve the inside of a tube reads as a lit tunnel the
 * moment the glass becomes see-through — which is precisely the failure this whole
 * pass exists to remove. Real optics are flat black inside for the same reason.
 */
function boreSleeve(M, radius, z0, z1, y, seg = 16) {
  const m = new THREE.Mesh(tubeZ([[radius, z0], [radius, z1]], seg), M.bore);
  m.position.y = y;
  m.frustumCulled = false;
  m.renderOrder = 8;
  return m;
}

/** Cylinder whose axis runs along X. */
function rodX(r, x0, x1, seg = 10) {
  const g = rodZ(r, -(x1 - x0) / 2, (x1 - x0) / 2, seg);
  rotate(g, 0, Math.PI / 2, 0);
  return translate(g, (x0 + x1) / 2, 0, 0);
}

/** Rounded trigger guard built from four short chamfered bars. */
function triggerGuard(zFront, zBack, yTop, yBottom, width = 0.007) {
  const t = 0.006;
  const parts = [];
  const len = zBack - zFront;
  const drop = yTop - yBottom;
  // front strut (angled), bottom bar, rear strut
  parts.push(place(chamferBox(width, drop * 0.9, t, 0.0012), 0, yTop - drop * 0.45, zFront + 0.004, 0.30, 0, 0));
  parts.push(place(chamferBox(width, t, len * 0.72, 0.0012), 0, yBottom, zFront + len * 0.5));
  parts.push(place(chamferBox(width, drop * 0.7, t, 0.0012), 0, yTop - drop * 0.35, zBack - 0.003, -0.35, 0, 0));
  parts.push(place(chamferBox(width, t * 1.4, t * 2.2, 0.0012), 0, yBottom + 0.0015, zFront + len * 0.16));
  return mergeGeom(parts);
}

/** A curved box magazine: stacked segments with progressive lean. */
function curvedMagazine(w, d, segH, count, curvePerSeg, chamfer = 0.0022) {
  const parts = [];
  let y = 0;
  let z = 0;
  let ang = 0;
  for (let i = 0; i < count; i++) {
    ang += curvePerSeg;
    const g = chamferBox(w * (1 - i * 0.012), segH * 1.02, d, chamfer);
    rotate(g, ang, 0, 0);
    // Step down along the leaning axis so segments stay joined.
    const dy = Math.cos(ang) * segH;
    const dz = -Math.sin(ang) * segH;
    translate(g, 0, y - dy / 2, z + dz / 2);
    y -= dy;
    z += dz;
    parts.push(g);
    // Witness holes read as a row of dimples down the spine.
    if (i > 0) {
      parts.push(
        place(chamferBox(w * 0.28, 0.004, 0.004, 0.0008), 0, y + dy * 0.5, z - dz * 0.5 + d / 2, ang, 0, 0)
      );
    }
  }
  // Floorplate
  const g = chamferBox(w + 0.004, 0.010, d + 0.004, 0.0022);
  rotate(g, ang, 0, 0);
  translate(g, 0, y - 0.004, z + Math.sin(ang) * 0.004);
  parts.push(g);
  return { geom: mergeGeom(parts), tipY: y, tipZ: z };
}

/* -------------------------------------------------------------------------- */
/* assembly                                                                    */
/* -------------------------------------------------------------------------- */

function assemble(ctx, sets) {
  const root = new THREE.Group();
  const meshes = {};
  for (const [name, { geoms, mat }] of Object.entries(sets)) {
    if (!geoms.length) continue;
    const m = new THREE.Mesh(mergeGeom(geoms), mat);
    m.frustumCulled = false;
    m.castShadow = false;
    m.receiveShadow = false;
    m.renderOrder = 2;
    root.add(m);
    meshes[name] = m;
  }
  return { root, meshes };
}

function hdrBasic(r, g, b, opacity = 1) {
  const m = new THREE.MeshBasicMaterial({ toneMapped: false, transparent: true, opacity });
  m.color.setRGB(r, g, b);
  return m;
}

function materials(ctx) {
  return {
    metal: forgeMaterial(ctx, 'gunmetal'),
    dark: forgeMaterial(ctx, 'gunmetal', { color: 0x8a8f95, roughness: 0.62 }),
    poly: forgeMaterial(ctx, 'polymer'),
    tan: forgeMaterial(ctx, 'polymer', { color: 0x9a8763 }),
    /**
     * Optic glass.
     *
     * This was an *opaque* near-mirror metal (`metalness 0.85`, no transparency),
     * so aiming down any sight showed a black disc — the one thing a sight must
     * never do. Glass is a dielectric: metalness 0, and it has to actually let the
     * world through.
     *
     * `depthWrite: false` matters as much as the opacity. The viewmodel scene is
     * composited over the world with depth cleared but colour preserved, so a
     * transparent lens blends against the already-rendered world — but only if it
     * does not write depth and occlude the far lens element behind it.
     *
     * The faint cyan is the anti-reflective coating every modern optic carries; it
     * is what stops the glass reading as an empty hole.
     */
    lens: new THREE.MeshPhysicalMaterial({
      /**
       * `color` is the diffuse albedo, and glass has essentially none.
       *
       * At `0xbfe6f2` — near white — the lens was a sheet of white paper lit by the
       * viewmodel key light at 2.6 and the environment at 1.6. Measured through the
       * SR-12's two elements, a world pixel of (100, 71, 60) came out (144, 132, 122):
       * washed 40% brighter and almost fully desaturated. That is why the sight
       * picture read as a flat grey plate that did not change when the player turned
       * around. The tint has to live in the *specular* — which is what an AR coating
       * actually is, a reflection colour — while the body of the glass stays dark so
       * the world passes through it unchanged.
       */
      color: 0x0b1417,
      transparent: true,
      opacity: 0.10,
      roughness: 0.05,
      metalness: 0,
      depthWrite: false,
      envMapIntensity: 0.55,
      specularIntensity: 0.8,
      specularColor: 0x9fd8e8,
      transmission: 0,
      side: THREE.DoubleSide,
    }),
    /**
     * Optic tube interior. Anodised matte black, and deliberately NOT the shared
     * gunmetal: gunmetal now carries bright bare-steel edge wear, which turned the
     * inside of every sight into a lit tunnel once the glass became see-through.
     * A real optic's bore is flat black precisely so the shooter's eye ignores it.
     */
    bore: new THREE.MeshStandardMaterial({
      color: 0x050507,
      roughness: 1.0,
      metalness: 0,
      // Near zero: the sky's environment map is a sunset, and even a little of it on
      // a tube wall reads as a lit tunnel rather than the flat black a sight bore is
      // finished with precisely so the shooter's eye skips over it.
      envMapIntensity: 0.03,
      side: THREE.BackSide,
    }),
    // Reticles are emissive light sources, not painted dots. They are pushed into HDR
    // so the bloom pass picks them up — a 1.0 red dot next to a sunlit receiver reads
    // as a sticker, while a 6.0 one reads as an illuminated sight.
    dot: hdrBasic(3.1, 0.30, 0.12, 0.96),
    glow: hdrBasic(0.5, 2.9, 1.3, 0.9),
  };
}

/* -------------------------------------------------------------------------- */
/* VK-416 — assault rifle                                                      */
/* -------------------------------------------------------------------------- */

function buildAR(ctx) {
  const M = materials(ctx);
  const metal = [];
  const poly = [];
  const parts = {};

  /* ------------------------------ upper receiver ---------------------------- */
  metal.push(place(chamferBox(0.040, 0.044, 0.226, 0.0014), 0, 0, -0.015));
  // Reinforcing rib along the top shoulder — catches a long highlight.
  metal.push(place(chamferBox(0.026, 0.006, 0.220, 0.0010), 0, 0.0225, -0.015));
  metal.push(translate(picatinnyRail(0.196, 0.0212, 0.078), 0, 0.0215, 0));
  // Ejection port: recess walls + hinged dust cover on the right.
  metal.push(place(chamferBox(0.004, 0.024, 0.052, 0.0010), 0.0195, 0.004, -0.002));
  metal.push(place(chamferBox(0.005, 0.014, 0.014, 0.0012), 0.0205, 0.016, 0.026)); // brass deflector
  metal.push(translate(rodZ(0.0068, 0.026, 0.050, 10), 0.0225, 0.012, 0)); // forward assist
  metal.push(place(chamferBox(0.010, 0.014, 0.008, 0.0012), 0.0235, 0.012, 0.052));
  // Rear takedown lug + charging-handle channel
  metal.push(place(chamferBox(0.034, 0.020, 0.014, 0.0014), 0, 0.014, 0.093));

  /* ------------------------------ lower receiver ---------------------------- */
  metal.push(place(chamferBox(0.034, 0.050, 0.120, 0.0016), 0, -0.046, 0.028));
  metal.push(place(chamferBox(0.037, 0.064, 0.046, 0.0022), 0, -0.048, -0.006)); // magwell
  metal.push(place(chamferBox(0.042, 0.009, 0.052, 0.0026), 0, -0.079, -0.006)); // flared lip
  metal.push(translate(rodX(0.0055, 0.017, 0.023, 8), 0, -0.034, 0.008)); // mag release
  metal.push(place(chamferBox(0.006, 0.016, 0.020, 0.0012), -0.019, -0.036, 0.004)); // bolt catch
  metal.push(translate(rodX(0.0062, -0.024, -0.016, 8), 0, -0.026, 0.058)); // selector
  metal.push(place(chamferBox(0.005, 0.010, 0.026, 0.0010), -0.024, -0.026, 0.064)); // selector lever
  metal.push(translate(rodX(0.0042, -0.019, 0.019, 8), 0, -0.030, 0.086)); // rear takedown pin
  metal.push(translate(rodX(0.0042, -0.019, 0.019, 8), 0, -0.028, -0.024)); // front pivot pin
  metal.push(translate(triggerGuard(0.004, 0.062, -0.062, -0.092, 0.0075), 0, 0, 0));

  /* -------------------------------- pistol grip ----------------------------- */
  {
    const g = [];
    g.push(place(chamferBox(0.031, 0.108, 0.044, 0.0055), 0, -0.052, 0));
    g.push(place(chamferBox(0.033, 0.014, 0.034, 0.0035), 0, -0.108, 0.002)); // butt of grip
    g.push(place(chamferBox(0.034, 0.026, 0.014, 0.0030), 0, -0.010, -0.014)); // beavertail
    // finger swells on the front face
    for (let i = 0; i < 3; i++) {
      g.push(place(chamferBox(0.029, 0.012, 0.008, 0.0025), 0, -0.030 - i * 0.026, -0.021));
    }
    g.push(translate(gripTexture(0.024, 0.070, 3, 7, 0.0038, 0.0010), 0, -0.055, 0.0222));
    const geo = mergeGeom(g);
    rotate(geo, -0.44, 0, 0);
    translate(geo, 0, -0.060, 0.066);
    poly.push(geo);
  }

  /* ---------------------------------- stock --------------------------------- */
  metal.push(translate(rodZ(0.0168, 0.096, 0.250, 14), 0, -0.004, 0)); // buffer tube
  metal.push(translate(tubeZ([[0.021, 0.098], [0.021, 0.110], [0.0195, 0.113]], 12), 0, -0.004, 0)); // castle nut
  {
    const s = [];
    s.push(place(chamferBox(0.042, 0.062, 0.104, 0.0050), 0, -0.010, 0.196));
    s.push(place(chamferBox(0.036, 0.020, 0.070, 0.0035), 0, 0.024, 0.190)); // comb
    s.push(place(chamferBox(0.046, 0.088, 0.015, 0.0040), 0, -0.014, 0.252)); // butt pad
    s.push(place(chamferBox(0.048, 0.014, 0.010, 0.0025), 0, -0.052, 0.246)); // toe hook
    s.push(place(chamferBox(0.030, 0.024, 0.026, 0.0030), 0, -0.040, 0.166)); // release lever housing
    poly.push(mergeGeom(s));
  }
  // QD sling socket on the left of the stock.
  metal.push(place(ringZ(0.0062, 0.0022, 12, 6), -0.0215, -0.020, 0.208, 0, Math.PI / 2, 0));

  /* -------------------------------- handguard ------------------------------- */
  metal.push(translate(tubeZ([[0.0235, -0.150], [0.0235, -0.128], [0.021, -0.126]], 14), 0, 0, 0)); // barrel nut
  metal.push(translate(picatinnyRail(0.198, 0.0212, -0.132), 0, 0.0215, 0));
  {
    // Octagonal handguard: two vertical slotted side panels, a slotted belly panel
    // and four 45-degree fillets. The gaps are real geometry, so the silhouette
    // breaks up the way a modern rail does.
    const hgLen = 0.198;
    const hgZ = -0.231;
    const side = slottedPanel(0.036, 0.0055, hgLen, 5, 0.44);
    const left = rotate(side.clone(), 0, 0, Math.PI / 2);
    translate(left, -0.0212, 0.001, hgZ);
    metal.push(left);
    const right = rotate(side, 0, 0, Math.PI / 2);
    translate(right, 0.0212, 0.001, hgZ);
    metal.push(right);

    const belly = slottedPanel(0.032, 0.0055, hgLen, 5, 0.44);
    translate(belly, 0, -0.0205, hgZ);
    metal.push(belly);
    metal.push(translate(picatinnyRail(0.058, 0.0180, -0.268), 0, -0.0295, 0));

    for (const [sx, sy] of [[1, 1], [-1, 1], [1, -1], [-1, -1]]) {
      const f = chamferBox(0.019, 0.0055, hgLen, 0.0010);
      rotate(f, 0, 0, (sx * sy > 0 ? -1 : 1) * Math.PI * 0.25);
      translate(f, sx * 0.0166, sy * 0.0166, hgZ);
      metal.push(f);
    }
    metal.push(translate(tubeZ([[0.024, -0.334], [0.024, -0.328], [0.0205, -0.326]], 14), 0, 0, 0)); // end cap
    metal.push(place(ringZ(0.0062, 0.0022, 12, 6), -0.0225, -0.012, -0.300, 0, Math.PI / 2, 0));
  }

  /* ---------------------------- angled foregrip ----------------------------- */
  {
    const g = [];
    g.push(place(chamferBox(0.026, 0.062, 0.030, 0.0045), 0, -0.028, 0));
    g.push(place(chamferBox(0.030, 0.010, 0.036, 0.0030), 0, 0.002, 0));
    g.push(translate(gripTexture(0.019, 0.038, 3, 4, 0.0034, 0.0009), 0, -0.032, 0.0152));
    const geo = mergeGeom(g);
    rotate(geo, 0.62, 0, 0);
    translate(geo, 0, -0.030, -0.252);
    poly.push(geo);
  }

  /* --------------------------------- barrel --------------------------------- */
  metal.push(translate(rodZ(0.0092, -0.352, -0.320, 12), 0, 0, 0));
  metal.push(translate(rodZ(0.0080, -0.418, -0.348, 12), 0, 0, 0));
  metal.push(place(chamferBox(0.024, 0.026, 0.030, 0.0018), 0, 0.003, -0.338)); // gas block
  metal.push(translate(rodZ(0.0034, -0.336, -0.150, 8), 0, 0.0125, 0)); // gas tube
  // A2 flash hider: base ring plus three prongs, i.e. real slots.
  metal.push(pipeZ(0.0126, 0.0084, -0.442, -0.418, 12));
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + Math.PI / 6;
    const p = chamferBox(0.0075, 0.0075, 0.026, 0.0011);
    translate(p, Math.cos(a) * 0.0105, Math.sin(a) * 0.0105, -0.455);
    metal.push(p);
  }
  metal.push(pipeZ(0.0126, 0.0090, -0.470, -0.462, 12));

  /* --------------------------------- optic ---------------------------------- */
  const SIGHT_Y = 0.0547;
  {
    const o = [];
    o.push(place(chamferBox(0.026, 0.009, 0.050, 0.0016), 0, 0.0337, -0.030)); // mount
    o.push(place(chamferBox(0.030, 0.010, 0.013, 0.0016), 0, 0.0330, -0.049)); // clamp
    o.push(place(chamferBox(0.008, 0.010, 0.011, 0.0012), -0.017, 0.0330, -0.049)); // throw lever
    metal.push(mergeGeom(o));

    const body = [];
    body.push(pipeZ(0.0178, 0.0150, -0.058, -0.004));
    body.push(translate(tubeZ([[0.0150, -0.058], [0.0196, -0.058], [0.0196, -0.050], [0.0178, -0.049]], 14), 0, 0, 0));
    body.push(translate(tubeZ([[0.0178, -0.013], [0.0196, -0.012], [0.0196, -0.004], [0.0150, -0.004]], 14), 0, 0, 0));
    body.push(place(chamferBox(0.010, 0.012, 0.012, 0.0012), 0, 0.019, -0.030)); // elevation turret
    body.push(place(chamferBox(0.012, 0.010, 0.012, 0.0012), -0.019, 0.0, -0.030)); // windage turret
    body.push(place(chamferBox(0.014, 0.007, 0.024, 0.0010), 0.017, 0.006, -0.024)); // battery cap boss
    const bg = mergeGeom(body);
    translate(bg, 0, SIGHT_Y, 0);
    metal.push(bg);

    // Matte-black bore sleeve inside the tube wall, so looking through the glass
    // shows a clean sight picture instead of the lit interior of the housing. Held
    // 0.5 mm clear of the pipe's own inner wall at 0.0150: at 0.1 mm the two surfaces
    // z-fought, which speckled the inside of the tube with bright gunmetal.
    const bore = new THREE.Mesh(tubeZ([[0.0145, -0.0570], [0.0145, -0.0050]], 16), M.bore);
    bore.position.y = SIGHT_Y;
    bore.frustumCulled = false;
    bore.renderOrder = 8;
    parts.bore = bore;

    const lensFront = new THREE.Mesh(discZ(0.0150, -0.0565, 14, -1), M.lens);
    lensFront.position.y = SIGHT_Y;
    lensFront.frustumCulled = false;
    lensFront.renderOrder = 20;
    parts.lensFront = lensFront;
    const lensRear = new THREE.Mesh(discZ(0.0150, -0.0055, 14, 1), M.lens);
    lensRear.position.y = SIGHT_Y;
    lensRear.frustumCulled = false;
    lensRear.renderOrder = 21;
    parts.lensRear = lensRear;

    // Reflex dot. Drawn without depth test so it always reads through the glass,
    // which is what a collimated sight actually looks like.
    const dotGeo = new THREE.CircleGeometry(0.0016, 10);
    const dot = new THREE.Mesh(dotGeo, M.dot);
    dot.position.set(0, SIGHT_Y, -0.030);
    dot.renderOrder = 60;
    dot.material.depthTest = false;
    dot.material.depthWrite = false;
    dot.frustumCulled = false;
    parts.reticle = dot;
  }

  /* ------------------------------ animated parts ---------------------------- */
  {
    const b = [];
    b.push(place(chamferBox(0.028, 0.024, 0.086, 0.0014), 0.004, 0.004, 0.012));
    b.push(place(chamferBox(0.014, 0.016, 0.020, 0.0012), 0.004, 0.004, -0.036)); // bolt head
    const bolt = new THREE.Mesh(mergeGeom(b), M.dark);
    bolt.frustumCulled = false;
    parts.bolt = bolt;
  }
  {
    const c = [];
    c.push(place(chamferBox(0.030, 0.008, 0.048, 0.0012), 0, 0.019, 0.086));
    c.push(place(chamferBox(0.062, 0.007, 0.013, 0.0012), 0, 0.019, 0.106));
    c.push(place(chamferBox(0.014, 0.010, 0.011, 0.0012), -0.030, 0.019, 0.104));
    const ch = new THREE.Mesh(mergeGeom(c), M.metal);
    ch.frustumCulled = false;
    parts.charging = ch;
  }
  {
    const t = new THREE.Mesh(
      mergeGeom([place(chamferBox(0.0065, 0.026, 0.009, 0.0012), 0, -0.011, 0)]),
      M.dark
    );
    t.position.set(0, -0.062, 0.040);
    t.frustumCulled = false;
    parts.trigger = t;
  }
  {
    const mag = curvedMagazine(0.0248, 0.0355, 0.052, 4, 0.075, 0.0022);
    const mg = new THREE.Group();
    const mm = new THREE.Mesh(mag.geom, M.poly);
    mm.frustumCulled = false;
    mg.add(mm);
    mg.position.set(0, -0.072, -0.006);
    parts.magazine = mg;
    parts.magGeom = mag.geom;
    parts.magMat = M.poly;
  }

  const built = assemble(ctx, { metal: { geoms: metal, mat: M.metal }, poly: { geoms: poly, mat: M.poly } });
  const root = built.root;
  for (const k of ['bolt', 'charging', 'trigger', 'magazine', 'bore', 'lensFront', 'lensRear', 'reticle']) {
    if (parts[k]) root.add(parts[k]);
  }

  const hands = attachHands(ctx, root, parts, {
    // Anchors are points on the weapon, solved so the trigger finger lands inside the
    // guard and the support hand's fingers close over the handguard's far flank.
    right: { anchor: [0.030, -0.081, 0.088], curl: 0.98, trigger: 0.75 },
    left: { anchor: [-0.036, -0.022, -0.248], curl: 0.95, spread: 0.35 },
  });

  return {
    root,
    parts,
    hands,
    // A reflex sight is collimated: the dot is projected to infinity, so it stays on
    // target no matter where the eye sits in the eyebox. Iron sights and the scope's
    // etched reticle are physical objects and must not get the same treatment.
    reticleCollimated: true,
    reticleClear: 0.0128, // how far the dot may drift before it would leave the glass
    aimPoint: new THREE.Vector3(0, SIGHT_Y, -0.030),
    muzzlePoint: new THREE.Vector3(0, 0, -0.470),
    ejectPoint: new THREE.Vector3(0.024, 0.006, -0.002),
    ejectDir: new THREE.Vector3(0.86, 0.34, 0.38),
    magOffset: new THREE.Vector3(0, -0.072, -0.006),
  };
}

/* -------------------------------------------------------------------------- */
/* MP-9K — submachine gun                                                      */
/* -------------------------------------------------------------------------- */

function buildSMG(ctx) {
  const M = materials(ctx);
  const metal = [];
  const poly = [];
  const parts = {};

  /* ---------------------------- receiver (round) ---------------------------- */
  // A stamped tubular receiver: the shape that makes an MP5 read at a glance.
  metal.push(translate(tubeZ([[0.0, -0.120], [0.0215, -0.118], [0.0215, 0.052], [0.0, 0.054]], 16), 0, 0.001, 0));
  metal.push(place(chamferBox(0.030, 0.012, 0.180, 0.0012), 0, 0.020, -0.032)); // top spine
  metal.push(translate(picatinnyRail(0.130, 0.0196, 0.036), 0, 0.0245, 0));
  // Ejection port + shell deflector
  metal.push(place(chamferBox(0.004, 0.020, 0.044, 0.0010), 0.0205, 0.006, -0.010));
  metal.push(place(chamferBox(0.008, 0.012, 0.012, 0.0012), 0.0215, 0.017, 0.014));
  // Cocking-handle tube on the left, angled up at the front — MP5's signature.
  metal.push(translate(rodZ(0.0088, -0.220, -0.120, 10), -0.0205, 0.014, 0));
  metal.push(place(chamferBox(0.014, 0.014, 0.026, 0.0014), -0.0205, 0.014, -0.226));

  /* ------------------------------ lower / grip ------------------------------ */
  metal.push(place(chamferBox(0.032, 0.038, 0.118, 0.0018), 0, -0.031, 0.006));
  metal.push(place(chamferBox(0.035, 0.056, 0.042, 0.0022), 0, -0.036, -0.044)); // magwell
  metal.push(place(chamferBox(0.039, 0.008, 0.048, 0.0024), 0, -0.062, -0.044));
  metal.push(translate(triggerGuard(-0.024, 0.030, -0.048, -0.076, 0.0072), 0, 0, 0));
  metal.push(translate(rodX(0.0056, -0.021, 0.021, 8), 0, -0.020, 0.038)); // selector spindle
  metal.push(place(chamferBox(0.006, 0.011, 0.024, 0.0012), -0.021, -0.020, 0.044));

  {
    const g = [];
    g.push(place(chamferBox(0.030, 0.098, 0.042, 0.0055), 0, -0.048, 0));
    g.push(place(chamferBox(0.032, 0.013, 0.033, 0.0035), 0, -0.099, 0.002));
    for (let i = 0; i < 3; i++) {
      g.push(place(chamferBox(0.028, 0.011, 0.008, 0.0025), 0, -0.026 - i * 0.024, -0.020));
    }
    g.push(translate(gripTexture(0.022, 0.062, 3, 6, 0.0036, 0.0009), 0, -0.050, 0.0212));
    const geo = mergeGeom(g);
    rotate(geo, -0.40, 0, 0);
    translate(geo, 0, -0.048, 0.036);
    poly.push(geo);
  }

  /* ------------------------------ retracting stock -------------------------- */
  metal.push(translate(rodZ(0.0072, 0.054, 0.196, 8), -0.019, -0.010, 0));
  metal.push(translate(rodZ(0.0072, 0.054, 0.196, 8), 0.019, -0.010, 0));
  poly.push(place(chamferBox(0.052, 0.062, 0.016, 0.0040), 0, -0.012, 0.200));
  poly.push(place(chamferBox(0.044, 0.030, 0.030, 0.0035), 0, -0.010, 0.184));
  metal.push(place(ringZ(0.0060, 0.0021, 12, 6), -0.019, -0.026, 0.176, 0, Math.PI / 2, 0));

  /* -------------------------------- handguard ------------------------------- */
  {
    const h = [];
    h.push(place(chamferBox(0.038, 0.036, 0.150, 0.0050), 0, -0.006, -0.196));
    // Vent slots down each side
    for (let i = 0; i < 4; i++) {
      const z = -0.150 - i * 0.030;
      h.push(place(chamferBox(0.042, 0.010, 0.016, 0.0018), 0, 0.006, z));
    }
    h.push(place(chamferBox(0.034, 0.014, 0.030, 0.0035), 0, -0.024, -0.140)); // hand stop
    poly.push(mergeGeom(h));
  }
  metal.push(translate(picatinnyRail(0.062, 0.0180, -0.192), 0, -0.026, 0));

  /* --------------------------------- barrel --------------------------------- */
  metal.push(translate(rodZ(0.0105, -0.290, -0.268, 12), 0, 0, 0)); // tri-lug
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    const p = chamferBox(0.0065, 0.0065, 0.016, 0.0010);
    translate(p, Math.cos(a) * 0.0092, Math.sin(a) * 0.0092, -0.282);
    metal.push(p);
  }
  metal.push(translate(rodZ(0.0072, -0.268, -0.170, 10), 0, 0, 0));
  metal.push(pipeZ(0.0098, 0.0060, -0.310, -0.290, 12)); // muzzle nut

  /* ----------------------------- MP5-style sights --------------------------- */
  /**
   * Sight height.
   *
   * This was 0.0335, and the receiver's own Picatinny rail tops out at 0.0322 — so
   * the bottom half of the rear aperture looked straight down the length of the rail
   * and the sight picture was mostly rail teeth. Measured: every ray below -1 degree
   * terminated on it. Sights have to clear whatever they are bolted to; 0.0430 puts
   * the whole aperture above the rail, which is also why real optic-height irons sit
   * on risers.
   */
  const SIGHT_Y = 0.0430;
  /**
   * Rotary drum rear sight with a real aperture through it.
   *
   * The aperture was 4 mm in radius: at the aiming eye relief that is a 4.3-degree
   * peephole, and a peephole is exactly what an aperture sight must *not* read as on
   * screen. A real diopter works because the eye focuses past the blurred rim; we
   * have no way to blur it, so the game equivalent is the ghost ring — a wide hole
   * with a thin rim that the eye ignores. 8.8 mm doubles the sight picture.
   */
  metal.push(translate(pipeZ(0.0125, 0.0088, 0.014, 0.030, 16), 0, SIGHT_Y, 0));
  // Matte-black inside the aperture, so the hole reads as a hole.
  parts.bore = boreSleeve(M, 0.0087, 0.0135, 0.0305, SIGHT_Y, 16);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    metal.push(
      translate(plainBox(0.0035, 0.0035, 0.014), Math.cos(a) * 0.0125, SIGHT_Y + Math.sin(a) * 0.0125, 0.022)
    );
  }
  metal.push(place(chamferBox(0.020, 0.014, 0.014, 0.0014), 0, SIGHT_Y - 0.017, 0.022));
  // Hooded front post. The post's *tip* is the aiming point, so it stops exactly on
  // the sight axis — 1.5 mm proud of it, as it was, and the post covers the target.
  // Short and wide: a 28 mm-long, 9 mm hood 35 cm from the eye is a tunnel, not a
  // ring, and it swallows the middle of the sight picture.
  metal.push(translate(pipeZ(0.0120, 0.0102, -0.249, -0.235, 14), 0, SIGHT_Y, 0));
  metal.push(place(chamferBox(0.0022, 0.0110, 0.0035, 0.0006), 0, SIGHT_Y - 0.0055, -0.242));
  metal.push(place(chamferBox(0.022, 0.010, 0.020, 0.0014), 0, SIGHT_Y - 0.020, -0.242));

  /* ------------------------------ animated parts ---------------------------- */
  {
    const b = [];
    b.push(place(chamferBox(0.026, 0.022, 0.074, 0.0014), 0.002, 0.005, 0.002));
    const bolt = new THREE.Mesh(mergeGeom(b), M.dark);
    bolt.frustumCulled = false;
    parts.bolt = bolt;
  }
  {
    const c = [];
    c.push(place(chamferBox(0.013, 0.024, 0.030, 0.0016), -0.026, 0.014, -0.222));
    c.push(place(chamferBox(0.010, 0.010, 0.020, 0.0012), -0.021, 0.014, -0.212));
    const ch = new THREE.Mesh(mergeGeom(c), M.metal);
    ch.frustumCulled = false;
    parts.charging = ch;
  }
  {
    const t = new THREE.Mesh(mergeGeom([place(chamferBox(0.006, 0.024, 0.008, 0.0011), 0, -0.010, 0)]), M.dark);
    t.position.set(0, -0.048, 0.010);
    t.frustumCulled = false;
    parts.trigger = t;
  }
  {
    const mag = curvedMagazine(0.0232, 0.0330, 0.048, 4, 0.085, 0.0020);
    const mg = new THREE.Group();
    const mm = new THREE.Mesh(mag.geom, M.metal);
    mm.frustumCulled = false;
    mg.add(mm);
    mg.position.set(0, -0.058, -0.044);
    parts.magazine = mg;
    parts.magGeom = mag.geom;
    parts.magMat = M.metal;
  }

  const built = assemble(ctx, { metal: { geoms: metal, mat: M.metal }, poly: { geoms: poly, mat: M.poly } });
  const root = built.root;
  for (const k of ['bolt', 'charging', 'trigger', 'magazine', 'bore']) if (parts[k]) root.add(parts[k]);

  const hands = attachHands(ctx, root, parts, {
    right: { anchor: [0.030, -0.066, 0.055], curl: 0.98, trigger: 0.75 },
    left: { anchor: [-0.034, -0.028, -0.196], curl: 0.95, spread: 0.3 },
  });

  return {
    root,
    parts,
    hands,
    aimPoint: new THREE.Vector3(0, SIGHT_Y, 0.022),
    muzzlePoint: new THREE.Vector3(0, 0, -0.312),
    ejectPoint: new THREE.Vector3(0.024, 0.008, -0.010),
    ejectDir: new THREE.Vector3(0.90, 0.30, 0.30),
    magOffset: new THREE.Vector3(0, -0.058, -0.044),
  };
}

/* -------------------------------------------------------------------------- */
/* SR-12 — marksman rifle                                                      */
/* -------------------------------------------------------------------------- */

function buildDMR(ctx) {
  const M = materials(ctx);
  const metal = [];
  const poly = [];
  const parts = {};

  /* ------------------------------ upper receiver ---------------------------- */
  metal.push(place(chamferBox(0.045, 0.050, 0.252, 0.0016), 0, 0.001, -0.024));
  metal.push(place(chamferBox(0.030, 0.007, 0.246, 0.0011), 0, 0.026, -0.024));
  metal.push(translate(picatinnyRail(0.244, 0.0212, 0.088), 0, 0.0250, 0));
  metal.push(place(chamferBox(0.005, 0.026, 0.060, 0.0011), 0.0222, 0.005, -0.006));
  metal.push(place(chamferBox(0.006, 0.016, 0.016, 0.0013), 0.0232, 0.019, 0.030));
  metal.push(translate(rodZ(0.0070, 0.030, 0.056, 10), 0.0252, 0.013, 0));
  metal.push(place(chamferBox(0.038, 0.022, 0.016, 0.0016), 0, 0.016, 0.104));

  /* ------------------------------ lower receiver ---------------------------- */
  metal.push(place(chamferBox(0.038, 0.054, 0.132, 0.0018), 0, -0.050, 0.032));
  metal.push(place(chamferBox(0.041, 0.070, 0.052, 0.0024), 0, -0.052, -0.010));
  metal.push(place(chamferBox(0.046, 0.010, 0.058, 0.0028), 0, -0.087, -0.010));
  metal.push(translate(rodX(0.0058, 0.019, 0.026, 8), 0, -0.036, 0.006));
  metal.push(place(chamferBox(0.006, 0.017, 0.022, 0.0013), -0.021, -0.038, 0.002));
  metal.push(translate(rodX(0.0064, -0.026, -0.018, 8), 0, -0.028, 0.062));
  metal.push(translate(triggerGuard(0.002, 0.068, -0.068, -0.100, 0.0080), 0, 0, 0));

  {
    const g = [];
    g.push(place(chamferBox(0.033, 0.112, 0.046, 0.0060), 0, -0.055, 0));
    g.push(place(chamferBox(0.035, 0.014, 0.036, 0.0035), 0, -0.113, 0.002));
    g.push(place(chamferBox(0.036, 0.028, 0.015, 0.0032), 0, -0.010, -0.015));
    for (let i = 0; i < 3; i++) {
      g.push(place(chamferBox(0.031, 0.012, 0.008, 0.0025), 0, -0.032 - i * 0.027, -0.022));
    }
    g.push(translate(gripTexture(0.026, 0.074, 3, 7, 0.0040, 0.0011), 0, -0.058, 0.0232));
    const geo = mergeGeom(g);
    rotate(geo, -0.42, 0, 0);
    translate(geo, 0, -0.066, 0.072);
    poly.push(geo);
  }

  /* ------------------------------ precision stock --------------------------- */
  metal.push(translate(rodZ(0.0175, 0.102, 0.256, 14), 0, -0.004, 0));
  {
    const s = [];
    s.push(place(chamferBox(0.044, 0.058, 0.150, 0.0055), 0, -0.014, 0.196));
    // Skeletonised: two lightening cuts leave a top rail and a bottom strut.
    s.push(place(chamferBox(0.040, 0.028, 0.118, 0.0040), 0, 0.030, 0.202)); // adjustable comb
    s.push(place(chamferBox(0.026, 0.030, 0.020, 0.0030), 0, 0.010, 0.140));
    s.push(place(chamferBox(0.050, 0.098, 0.018, 0.0045), 0, -0.018, 0.268)); // butt pad
    s.push(place(chamferBox(0.052, 0.016, 0.014, 0.0030), 0, -0.062, 0.260)); // adjustable toe
    s.push(place(chamferBox(0.020, 0.024, 0.060, 0.0030), 0, -0.048, 0.220)); // monopod rail
    poly.push(mergeGeom(s));
  }
  metal.push(place(ringZ(0.0064, 0.0023, 12, 6), -0.0225, -0.024, 0.212, 0, Math.PI / 2, 0));

  /* -------------------------------- handguard ------------------------------- */
  metal.push(translate(tubeZ([[0.0265, -0.172], [0.0265, -0.150], [0.023, -0.148]], 14), 0, 0, 0));
  metal.push(translate(picatinnyRail(0.256, 0.0212, -0.154), 0, 0.0250, 0));
  {
    const hgLen = 0.256;
    const hgZ = -0.282;
    const side = slottedPanel(0.042, 0.006, hgLen, 6, 0.46);
    const left = rotate(side.clone(), 0, 0, Math.PI / 2);
    translate(left, -0.0238, 0.001, hgZ);
    metal.push(left);
    const right = rotate(side, 0, 0, Math.PI / 2);
    translate(right, 0.0238, 0.001, hgZ);
    metal.push(right);
    const belly = slottedPanel(0.036, 0.006, hgLen, 6, 0.46);
    translate(belly, 0, -0.0235, hgZ);
    metal.push(belly);
    for (const [sx, sy] of [[1, 1], [-1, 1], [1, -1], [-1, -1]]) {
      const f = chamferBox(0.021, 0.006, hgLen, 0.0010);
      rotate(f, 0, 0, (sx * sy > 0 ? -1 : 1) * Math.PI * 0.25);
      translate(f, sx * 0.0188, sy * 0.0188, hgZ);
      metal.push(f);
    }
    metal.push(translate(tubeZ([[0.027, -0.414], [0.027, -0.408], [0.023, -0.406]], 14), 0, 0, 0));
    metal.push(translate(picatinnyRail(0.090, 0.0180, -0.320), 0, -0.0330, 0));
  }
  // Bipod stub, folded back along the handguard.
  metal.push(place(chamferBox(0.012, 0.010, 0.078, 0.0012), -0.010, -0.040, -0.360, 0.10, 0, 0));
  metal.push(place(chamferBox(0.012, 0.010, 0.078, 0.0012), 0.010, -0.040, -0.360, 0.10, 0, 0));

  /* ------------------------------- heavy barrel ----------------------------- */
  metal.push(translate(rodZ(0.0125, -0.470, -0.408, 14), 0, 0, 0));
  // Fluting: six shallow channels, which is what tells the eye "match barrel".
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const f = plainBox(0.005, 0.005, 0.056);
    translate(f, Math.cos(a) * 0.0125, Math.sin(a) * 0.0125, -0.440);
    metal.push(f);
  }
  metal.push(translate(rodZ(0.0105, -0.520, -0.470, 12), 0, 0, 0));
  // Muzzle brake with real ports.
  metal.push(pipeZ(0.0155, 0.0092, -0.590, -0.520, 14));
  for (let i = 0; i < 3; i++) {
    for (const sx of [-1, 1]) {
      const p = plainBox(0.006, 0.018, 0.010);
      translate(p, sx * 0.0128, 0, -0.532 - i * 0.018);
      metal.push(p);
    }
  }

  /* --------------------------------- scope ---------------------------------- */
  const SIGHT_Y = 0.0700;
  {
    const o = [];
    o.push(place(chamferBox(0.030, 0.011, 0.026, 0.0016), 0, 0.0345, -0.052));
    o.push(place(chamferBox(0.030, 0.011, 0.026, 0.0016), 0, 0.0345, 0.030));
    metal.push(mergeGeom(o));

    const body = [];
    /**
     * Outer surface as one continuous profile, front (objective) to rear (ocular).
     *
     * The clear cone an open tube can deliver is `atan(r / d)` at whichever internal
     * shoulder is tightest, so the two numbers that decide whether this scope can be
     * aimed through at all are the main tube's radius and how far away the objective
     * shoulder sits. It was a 31.6 mm tube with the shoulder 29 cm from the eye,
     * which clips the sight picture to 2.7 degrees. A 38 mm tube with the shoulder
     * pulled back to 26 cm and a 48 mm objective opens it to 3.6, and the objective
     * bell gets visibly heavier in the process — which is what a marksman optic
     * should look like anyway.
     */
    body.push(
      tubeZ(
        [
          [0.0238, -0.1200],
          [0.0265, -0.1160],
          [0.0265, -0.0680],
          [0.0190, -0.0600],
          [0.0190, 0.0580],
          [0.0212, 0.0660],
          [0.0212, 0.0920],
          [0.0186, 0.0960],
        ],
        18
      )
    );
    // Inner wall of the ocular bell so the lens sits in a real recess. The objective
    // end deliberately has none: it is 24 cm down the sight line, so an inward-facing
    // ring there draws a bright halo right in the middle of the sight picture. The
    // bell's own outer cone is back-facing from inside the tube and costs nothing.
    body.push(tubeZ([[0.0186, 0.0960], [0.0186, 0.0940]], 16));
    // Elevation turret (axis vertical) and windage turret (axis lateral).
    //
    // Both start *outside* the tube wall (r = 0.0158). Started at 0.0150 they closed
    // their base disc across the top and side of the bore, putting a lit cap 8 mm
    // inside the sight picture.
    {
      const t = tubeZ([[0.0, 0.0], [0.0138, 0.0], [0.0138, 0.0170], [0.0122, 0.0184], [0.0, 0.0184]], 12);
      t.rotateX(-Math.PI / 2);
      t.translate(0, 0.0206, -0.030);
      body.push(t);
    }
    {
      // `rotateY(+PI/2)` maps the lathe's +Z onto +X, so the windage turret grew
      // *inboard*: its body ran from x = -0.0150 to x = +0.0014, i.e. straight
      // through the middle of the bore, and its capped base sat 15 mm inside the
      // sight picture. Mirrored, it grows outboard from the tube wall like the
      // elevation turret already did.
      const t = tubeZ([[0.0, 0.0], [0.0126, 0.0], [0.0126, 0.0150], [0.0110, 0.0164], [0.0, 0.0164]], 12);
      t.rotateY(-Math.PI / 2);
      t.translate(-0.0196, 0, -0.030);
      body.push(t);
    }
    /**
     * Turret saddle.
     *
     * This was `chamferBox(0.034, 0.034, 0.016)` centred on (0, SIGHT_Y, -0.030) — a
     * solid 34 x 34 mm block straight through the middle of a tube whose inner radius
     * is 15.8 mm. Every ray from the eye down the bore terminated on its rear face at
     * z = -0.022; the scope was 100% opaque, which is the "pure black scope" the
     * player reported. A saddle is a *collar around* the tube, never a plug through
     * it, so it is built as one: a short thick-walled ring flush with the tube wall.
     */
    // Built from an outer wall and two end faces rather than `pipeZ`: a pipe carries
    // an inward-facing inner wall, and anything inward-facing inside this tube is
    // drawn straight into the sight picture.
    body.push(tubeZ([[0.0250, -0.0380], [0.0250, -0.0220]], 18));
    body.push(tubeZ([[0.0196, -0.0380], [0.0250, -0.0380]], 18));
    body.push(tubeZ([[0.0250, -0.0220], [0.0196, -0.0220]], 18));
    // Bosses for the turret bases, kept clear of the bore for the same reason.
    for (const [dx, dy] of [[0, 0.0246], [-0.0246, 0]]) {
      body.push(place(chamferBox(dx ? 0.010 : 0.026, dy ? 0.010 : 0.026, 0.018, 0.0012), dx, dy, -0.030));
    }
    // Magnification ring: knurled band around the ocular.
    body.push(tubeZ([[0.0228, 0.0380], [0.0228, 0.0580]], 16));
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      body.push(translate(plainBox(0.004, 0.004, 0.020), Math.cos(a) * 0.0228, Math.sin(a) * 0.0228, 0.048));
    }
    const bg = mergeGeom(body);
    translate(bg, 0, SIGHT_Y, 0);
    metal.push(bg);

    /**
     * Ocular baffle — and only the ocular.
     *
     * A sleeve down the full 245 mm of the tube is the obvious thing to build and it
     * is wrong: it turns the scope into a drinking straw. The clear cone through a
     * tube is `atan(r / d)` at its *far* rim, so a 15.6 mm sleeve ending 34 cm from
     * the eye clips the sight picture to 2.3 degrees — a keyhole, measured. Lining
     * only the ocular bell moves the limiting rim to 15 cm and opens the picture to
     * about 6 degrees.
     *
     * Nothing is lost by leaving the rest of the tube bare: the scope body is a
     * single-sided surface of revolution, so from *inside* the bore its wall is
     * back-facing and never rasterised. The eye looks straight out to the world,
     * which is exactly what a real scope's optics deliver and what an open tube
     * cannot. The baffle still does its job — it hides the lit inside of the ocular
     * bell and stops the glass reading as a hole through the weapon at hip angles.
     */
    parts.bore = boreSleeve(M, 0.0184, 0.0655, 0.0950, SIGHT_Y, 18);

    /**
     * The objective shoulder, blacked out.
     *
     * The cone where the objective bell steps down to the main tube is the one
     * inward-facing surface the eye can see all the way down the bore, and in
     * gunmetal it rendered as a lit grey plate covering most of the sight picture —
     * measured at model z = -0.10, filling everything outside 2.7 degrees. This
     * shadows it 0.3 mm inside, so it occludes without changing the clear cone.
     */
    {
      const s = new THREE.Mesh(
        tubeZ([[0.0262, -0.1150], [0.0262, -0.0685], [0.0187, -0.0605]], 18),
        M.bore
      );
      s.position.y = SIGHT_Y;
      s.frustumCulled = false;
      s.renderOrder = 8;
      parts.boreFront = s;
    }

    const objective = new THREE.Mesh(discZ(0.0238, -0.1185, 18, -1), M.lens);
    objective.position.y = SIGHT_Y;
    objective.frustumCulled = false;
    objective.renderOrder = 20;
    parts.lensFront = objective;
    const ocular = new THREE.Mesh(discZ(0.0186, 0.0955, 16, 1), M.lens);
    ocular.position.y = SIGHT_Y;
    ocular.frustumCulled = false;
    ocular.renderOrder = 21;
    parts.lensRear = ocular;

    /**
     * Scope shadow. A magnified optic shows a black ring creeping in from the edge of
     * the ocular unless the eye is perfectly centred in the eyebox, and that ring is
     * what the eye reads as "this sight magnifies". Drawn as a radial alpha ramp over
     * the ocular so the centre of the sight picture stays completely clear.
     */
    {
      const sh = new THREE.Mesh(
        new THREE.CircleGeometry(0.0192, 32),
        new THREE.MeshBasicMaterial({
          map: vignetteTexture(),
          color: 0x000000,
          transparent: true,
          depthTest: false,
          depthWrite: false,
          toneMapped: false,
        })
      );
      sh.position.set(0, SIGHT_Y, 0.0952);
      sh.renderOrder = 40;
      sh.frustumCulled = false;
      parts.scopeShadow = sh;
    }

    // Mil-dot reticle drawn as thin quads, depth-test off so it reads through glass.
    const ret = new THREE.Group();
    const rmat = new THREE.MeshBasicMaterial({ color: 0x0a0a0a, toneMapped: false });
    const mk = (w, h, x, y) => {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), rmat);
      m.position.set(x, y, 0);
      return m;
    };
    ret.add(mk(0.0008, 0.030, 0, 0));
    ret.add(mk(0.030, 0.0008, 0, 0));
    for (const d of [-0.011, -0.0055, 0.0055, 0.011]) {
      ret.add(mk(0.0022, 0.0022, d, 0));
      ret.add(mk(0.0022, 0.0022, 0, d));
    }
    ret.position.set(0, SIGHT_Y, 0.090);
    ret.renderOrder = 60;
    ret.traverse((o) => {
      if (o.isMesh) {
        o.material.depthTest = false;
        o.material.depthWrite = false;
        o.frustumCulled = false;
      }
    });
    parts.reticle = ret;
  }

  /* ------------------------------ animated parts ---------------------------- */
  {
    const bolt = new THREE.Mesh(
      mergeGeom([
        place(chamferBox(0.032, 0.028, 0.098, 0.0015), 0.004, 0.005, 0.014),
        place(chamferBox(0.016, 0.018, 0.022, 0.0012), 0.004, 0.005, -0.042),
      ]),
      M.dark
    );
    bolt.frustumCulled = false;
    parts.bolt = bolt;
  }
  {
    const ch = new THREE.Mesh(
      mergeGeom([
        place(chamferBox(0.034, 0.009, 0.052, 0.0013), 0, 0.021, 0.096),
        place(chamferBox(0.068, 0.008, 0.014, 0.0013), 0, 0.021, 0.118),
        place(chamferBox(0.015, 0.011, 0.012, 0.0012), -0.033, 0.021, 0.116),
      ]),
      M.metal
    );
    ch.frustumCulled = false;
    parts.charging = ch;
  }
  {
    const t = new THREE.Mesh(mergeGeom([place(chamferBox(0.007, 0.028, 0.010, 0.0012), 0, -0.012, 0)]), M.dark);
    t.position.set(0, -0.068, 0.044);
    t.frustumCulled = false;
    parts.trigger = t;
  }
  {
    const mag = curvedMagazine(0.0272, 0.0400, 0.050, 4, 0.055, 0.0024);
    const mg = new THREE.Group();
    const mm = new THREE.Mesh(mag.geom, M.metal);
    mm.frustumCulled = false;
    mg.add(mm);
    mg.position.set(0, -0.080, -0.010);
    parts.magazine = mg;
    parts.magGeom = mag.geom;
    parts.magMat = M.metal;
  }

  const built = assemble(ctx, { metal: { geoms: metal, mat: M.metal }, poly: { geoms: poly, mat: M.poly } });
  const root = built.root;
  for (const k of ['bolt', 'charging', 'trigger', 'magazine', 'bore', 'boreFront', 'lensFront', 'lensRear', 'scopeShadow', 'reticle']) {
    if (parts[k]) root.add(parts[k]);
  }

  const hands = attachHands(ctx, root, parts, {
    right: { anchor: [0.030, -0.086, 0.094], curl: 0.98, trigger: 0.70 },
    left: { anchor: [-0.039, -0.020, -0.282], curl: 0.95, spread: 0.35 },
  });

  return {
    root,
    parts,
    hands,
    reticleCollimated: false, // a scope reticle sits in the tube, not at infinity
    aimPoint: new THREE.Vector3(0, SIGHT_Y, 0.070),
    muzzlePoint: new THREE.Vector3(0, 0, -0.592),
    ejectPoint: new THREE.Vector3(0.027, 0.008, -0.006),
    ejectDir: new THREE.Vector3(0.88, 0.36, 0.30),
    magOffset: new THREE.Vector3(0, -0.080, -0.010),
  };
}

/* -------------------------------------------------------------------------- */
/* M90 — pump shotgun                                                          */
/* -------------------------------------------------------------------------- */

function buildShotgun(ctx) {
  const M = materials(ctx);
  const metal = [];
  const poly = [];
  const parts = {};

  /* --------------------------------- receiver ------------------------------- */
  metal.push(place(chamferBox(0.044, 0.058, 0.196, 0.0022), 0, -0.014, 0.014));
  metal.push(place(chamferBox(0.046, 0.014, 0.140, 0.0018), 0, 0.014, 0.008)); // top strap
  metal.push(place(chamferBox(0.006, 0.026, 0.070, 0.0012), 0.0215, -0.010, 0.006)); // ejection port
  metal.push(place(chamferBox(0.040, 0.012, 0.062, 0.0016), 0, -0.042, -0.020)); // loading gate
  metal.push(translate(picatinnyRail(0.116, 0.0196, 0.070), 0, 0.0205, 0));
  metal.push(translate(triggerGuard(0.028, 0.098, -0.046, -0.078, 0.0085), 0, 0, 0));
  metal.push(translate(rodX(0.0060, -0.023, 0.023, 8), 0, -0.038, 0.096)); // safety cross-bolt

  /* -------------------------------- stock ----------------------------------- */
  {
    const s = [];
    // Straight pistol-grip stock with a comb: one continuous swept form.
    s.push(place(chamferBox(0.040, 0.070, 0.096, 0.0060), 0, -0.036, 0.152, -0.14, 0, 0));
    s.push(place(chamferBox(0.042, 0.076, 0.110, 0.0060), 0, -0.052, 0.246, -0.12, 0, 0));
    s.push(place(chamferBox(0.044, 0.040, 0.060, 0.0045), 0, -0.012, 0.156, -0.20, 0, 0)); // comb
    s.push(place(chamferBox(0.048, 0.098, 0.018, 0.0050), 0, -0.066, 0.300, -0.10, 0, 0)); // recoil pad
    // Pistol-grip swell under the receiver tang.
    s.push(place(chamferBox(0.034, 0.070, 0.046, 0.0055), 0, -0.078, 0.126, -0.55, 0, 0));
    s.push(translate(gripTexture(0.026, 0.046, 3, 5, 0.0042, 0.0011), 0, -0.088, 0.148));
    poly.push(mergeGeom(s));
  }
  metal.push(place(ringZ(0.0068, 0.0024, 12, 6), 0, -0.072, 0.284, 0, Math.PI / 2, 0));

  /* --------------------------- barrel + magazine tube ----------------------- */
  metal.push(translate(rodZ(0.0128, -0.470, -0.086, 14), 0, 0, 0)); // barrel
  metal.push(translate(tubeZ([[0.0128, -0.094], [0.0165, -0.086], [0.0165, -0.070], [0.0150, -0.066]], 14), 0, 0, 0));
  metal.push(translate(rodZ(0.0125, -0.420, -0.080, 12), 0, -0.0268, 0)); // mag tube
  metal.push(translate(tubeZ([[0.0125, -0.436], [0.0155, -0.428], [0.0155, -0.420]], 12), 0, -0.0268, 0)); // end cap
  metal.push(place(chamferBox(0.024, 0.030, 0.020, 0.0016), 0, -0.014, -0.300)); // barrel clamp
  metal.push(place(chamferBox(0.024, 0.030, 0.020, 0.0016), 0, -0.014, -0.150));
  // Heat shield: a slotted shroud over the barrel, one of the most recognisable
  // shotgun silhouettes there is.
  {
    const hs = [];
    for (let i = 0; i < 6; i++) {
      const z = -0.142 - i * 0.046;
      hs.push(place(chamferBox(0.034, 0.010, 0.020, 0.0012), 0, 0.0108, z));
      hs.push(place(chamferBox(0.010, 0.034, 0.020, 0.0012), 0.0148, 0.0, z));
      hs.push(place(chamferBox(0.010, 0.034, 0.020, 0.0012), -0.0148, 0.0, z));
    }
    hs.push(place(chamferBox(0.008, 0.008, 0.290, 0.0010), 0.0125, 0.0092, -0.260));
    hs.push(place(chamferBox(0.008, 0.008, 0.290, 0.0010), -0.0125, 0.0092, -0.260));
    metal.push(mergeGeom(hs));
  }

  /* ------------------------------- ghost ring sights ------------------------ */
  // Raised above the receiver rail (teeth top out at 0.0282) for the same reason as
  // the SMG's: at 0.0330 the lower half of the ring looked straight along the rail.
  const SIGHT_Y = 0.0400;
  // A ghost ring's whole point is that the rim is too big and too close to focus on,
  // so the eye ignores it and centres the bead by itself. A 5 mm hole is a peephole
  // instead; 7.4 mm with a thinner rim is a ghost ring.
  metal.push(place(ringZ(0.0094, 0.0020, 16, 6), 0, SIGHT_Y, 0.062));
  metal.push(place(chamferBox(0.020, 0.018, 0.012, 0.0014), 0, SIGHT_Y - 0.018, 0.062));
  metal.push(place(chamferBox(0.0026, 0.0140, 0.0040, 0.0006), 0, SIGHT_Y - 0.007, -0.436));
  metal.push(place(chamferBox(0.016, 0.014, 0.014, 0.0012), 0, SIGHT_Y - 0.021, -0.436));
  {
    // Front bead: a tiny bright dome — the only thing you actually look at, so it
    // sits on the sight axis rather than proud of it.
    const bead = new THREE.Mesh(new THREE.SphereGeometry(0.0022, 8, 6), M.glow);
    bead.position.set(0, SIGHT_Y, -0.436);
    bead.renderOrder = 60;
    bead.material.depthTest = false;
    bead.frustumCulled = false;
    parts.reticle = bead;
  }

  /* --------------------------- pump forend (animated) ----------------------- */
  {
    const p = [];
    p.push(place(chamferBox(0.048, 0.046, 0.120, 0.0060), 0, -0.020, 0));
    // Longitudinal ribs
    for (let i = -3; i <= 3; i++) {
      p.push(place(plainBox(0.0055, 0.0055, 0.112), i * 0.0072, -0.0435, 0));
    }
    p.push(place(chamferBox(0.052, 0.050, 0.012, 0.0040), 0, -0.020, 0.058));
    p.push(place(chamferBox(0.052, 0.050, 0.012, 0.0040), 0, -0.020, -0.058));
    const pm = new THREE.Mesh(mergeGeom(p), M.poly);
    pm.frustumCulled = false;
    const pg = new THREE.Group();
    pg.add(pm);
    pg.position.set(0, 0, -0.230);
    parts.pump = pg;
  }

  /* ------------------------------ animated parts ---------------------------- */
  {
    const t = new THREE.Mesh(mergeGeom([place(chamferBox(0.0075, 0.028, 0.010, 0.0013), 0, -0.012, 0)]), M.dark);
    t.position.set(0, -0.046, 0.068);
    t.frustumCulled = false;
    parts.trigger = t;
  }
  {
    // The elevator/carrier visible through the loading gate.
    const c = new THREE.Mesh(mergeGeom([place(chamferBox(0.030, 0.008, 0.056, 0.0012), 0, 0, 0)]), M.dark);
    c.position.set(0, -0.036, -0.018);
    c.frustumCulled = false;
    parts.carrier = c;
  }
  {
    // A loose shell held in the loading hand during shell-by-shell reloads.
    const sg = new THREE.Group();
    const hull = new THREE.Mesh(
      rodZ(0.0098, -0.030, 0.006, 12),
      forgeMaterial(ctx, 'polymer', { color: 0x7a1d16, roughness: 0.55 })
    );
    sg.add(hull);
    const head = new THREE.Mesh(rodZ(0.0104, 0.004, 0.014, 12), M.dark);
    sg.add(head);
    sg.visible = false;
    sg.traverse((o) => (o.frustumCulled = false));
    parts.loadShell = sg;
  }

  const built = assemble(ctx, { metal: { geoms: metal, mat: M.metal }, poly: { geoms: poly, mat: M.poly } });
  const root = built.root;
  for (const k of ['pump', 'trigger', 'carrier', 'reticle', 'loadShell']) if (parts[k]) root.add(parts[k]);

  const hands = attachHands(ctx, root, parts, {
    right: { anchor: [0.030, -0.060, 0.126], curl: 0.98, trigger: 0.65 },
    // parented to the forend, so the support hand rides the pump stroke for free
    left: { anchor: [-0.039, -0.032, 0.0], curl: 0.95, spread: 0.3, parent: 'pump' },
  });

  return {
    root,
    parts,
    hands,
    aimPoint: new THREE.Vector3(0, SIGHT_Y, 0.062),
    muzzlePoint: new THREE.Vector3(0, 0, -0.472),
    ejectPoint: new THREE.Vector3(0.026, -0.008, 0.004),
    ejectDir: new THREE.Vector3(0.92, 0.24, 0.30),
    magOffset: new THREE.Vector3(0, -0.036, -0.018),
  };
}

/* -------------------------------------------------------------------------- */
/* MG-6 — light machine gun                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Belt of linked cartridges.
 *
 * Built as a swept row of case bodies with a link tab between each, following a
 * quadratic arc from the box mouth up into the feed tray. Cases are `plainBox`: a
 * 10 mm cartridge would spend its entire life under two pixels, so a 44-triangle
 * chamfered version would cost 500 triangles to render exactly the same silhouette.
 *
 * `sag` bows the middle of the run downward, which is the detail that stops a belt
 * reading as a ladder — a real belt hangs.
 */
function ammoBelt(count, from, to, sag = 0.012) {
  const parts = [];
  for (let i = 0; i < count; i++) {
    const t = count > 1 ? i / (count - 1) : 0;
    const x = THREE.MathUtils.lerp(from[0], to[0], t);
    const y = THREE.MathUtils.lerp(from[1], to[1], t) - Math.sin(t * Math.PI) * sag;
    const z = THREE.MathUtils.lerp(from[2], to[2], t);
    // Lean each round along the local tangent so the run curves rather than steps.
    const lean = (t - 0.5) * 0.5;
    parts.push(place(plainBox(0.0092, 0.030, 0.0112), x, y, z, lean, 0, 0));
    parts.push(place(plainBox(0.0104, 0.008, 0.0124), x, y + 0.013, z, lean, 0, 0)); // link tab
    parts.push(place(plainBox(0.0060, 0.014, 0.0070), x, y - 0.020, z, lean, 0, 0)); // bullet tip
  }
  return mergeGeom(parts);
}

function buildLMG(ctx) {
  const M = materials(ctx);
  const metal = [];
  const poly = [];
  const parts = {};

  /**
   * Sight height.
   *
   * 70 mm, the tallest in the file, and it has to be. A belt-fed weapon's optic sits
   * on top of a feed tray cover that is itself on top of a receiver deep enough to
   * pass a belt through, so the rail here tops out at 45 mm where the rifle's tops
   * out at 29. Every ray inside the tube's own 4.8-degree clear cone is checked
   * against the rail, the carry handle and the gas block below (see the clearance
   * notes on each): the tightest is the carry handle at 7 mm.
   */
  const SIGHT_Y = 0.070;

  /* -------------------------------- receiver -------------------------------- */
  metal.push(place(chamferBox(0.052, 0.058, 0.300, 0.0018), 0, -0.004, -0.040));
  // Feed tray cover: the hinged lid a belt is laid into. Its top is the mounting
  // surface, so it carries the rail rather than the receiver doing it.
  metal.push(place(chamferBox(0.050, 0.018, 0.200, 0.0016), 0, 0.030, -0.070));
  metal.push(translate(rodX(0.0055, -0.026, 0.026, 8), 0, 0.030, -0.168)); // cover hinge pin
  metal.push(place(chamferBox(0.030, 0.016, 0.020, 0.0014), 0, 0.030, 0.026)); // cover latch
  metal.push(place(chamferBox(0.014, 0.010, 0.026, 0.0012), 0.024, 0.030, 0.022)); // latch lever
  // Rail at 0.0375: the teeth top out at 0.0452 and the lowest ray of the sight
  // picture passes 0.0533 at the rail's far end, so it stays out of the tube's cone.
  // 14 teeth rather than the 19 the default pitch would give: at this rail's length
  // the slot pattern reads identically and the 60 triangles are better spent on the
  // belt, which is the part of this weapon nobody has seen before.
  metal.push(translate(picatinnyRail(0.190, 0.0212, 0.020, 14), 0, 0.0375, 0));
  // Ejection port and the belt-feed chute opposite it.
  metal.push(place(chamferBox(0.005, 0.032, 0.078, 0.0012), 0.0265, -0.008, -0.028));
  metal.push(place(chamferBox(0.008, 0.014, 0.016, 0.0012), 0.0275, 0.012, 0.014)); // deflector
  metal.push(place(chamferBox(0.006, 0.040, 0.062, 0.0014), -0.0265, -0.012, -0.076));
  metal.push(place(chamferBox(0.010, 0.014, 0.020, 0.0012), -0.028, 0.010, -0.108)); // feed pawl housing
  // Stamped reinforcing ribs down the flanks — the detail that says "pressed steel".
  for (let i = 0; i < 2; i++) {
    const z = -0.126 + i * 0.100;
    metal.push(place(chamferBox(0.056, 0.010, 0.012, 0.0012), 0, -0.020, z));
  }
  // Rear takedown and the buffer housing the stock bolts onto.
  metal.push(place(chamferBox(0.046, 0.048, 0.028, 0.0016), 0, -0.006, 0.118));
  metal.push(translate(rodX(0.0048, -0.026, 0.026, 8), 0, -0.026, 0.106));

  /* ------------------------------ trigger group ----------------------------- */
  metal.push(place(chamferBox(0.040, 0.042, 0.128, 0.0018), 0, -0.050, 0.042));
  metal.push(translate(triggerGuard(0.010, 0.072, -0.066, -0.098, 0.0082), 0, 0, 0));
  metal.push(translate(rodX(0.0060, -0.024, 0.024, 8), 0, -0.036, 0.096)); // safety cross-bolt
  metal.push(place(chamferBox(0.006, 0.014, 0.028, 0.0012), -0.023, -0.030, 0.070)); // selector

  {
    const g = [];
    g.push(place(chamferBox(0.033, 0.112, 0.046, 0.0058), 0, -0.055, 0));
    g.push(place(chamferBox(0.035, 0.014, 0.036, 0.0035), 0, -0.113, 0.002));
    g.push(place(chamferBox(0.036, 0.026, 0.015, 0.0030), 0, -0.010, -0.016)); // beavertail
    for (let i = 0; i < 3; i++) {
      g.push(place(chamferBox(0.031, 0.012, 0.008, 0.0025), 0, -0.032 - i * 0.027, -0.023));
    }
    g.push(translate(gripTexture(0.026, 0.074, 3, 7, 0.0040, 0.0011), 0, -0.058, 0.0232));
    const geo = mergeGeom(g);
    rotate(geo, -0.42, 0, 0);
    translate(geo, 0, -0.064, 0.082);
    poly.push(geo);
  }

  /* ---------------------------------- stock --------------------------------- */
  {
    const s = [];
    s.push(place(chamferBox(0.048, 0.072, 0.156, 0.0055), 0, -0.016, 0.198));
    s.push(place(chamferBox(0.042, 0.026, 0.104, 0.0038), 0, 0.026, 0.186)); // comb
    s.push(place(chamferBox(0.054, 0.100, 0.020, 0.0050), 0, -0.020, 0.282)); // butt pad
    // Folding shoulder rest — the hooked plate that hangs under a SAW's buttstock and
    // is half of why the silhouette is recognisable at a glance.
    s.push(place(chamferBox(0.050, 0.014, 0.062, 0.0032), 0, -0.062, 0.252, 0.22, 0, 0));
    s.push(place(chamferBox(0.030, 0.026, 0.024, 0.0030), 0, -0.048, 0.166)); // hinge boss
    poly.push(mergeGeom(s));
  }
  metal.push(place(ringZ(0.0066, 0.0023, 12, 6), -0.0245, -0.024, 0.216, 0, Math.PI / 2, 0));

  /* ------------------------- belt box + belt (animated) --------------------- */
  {
    // The box is the "magazine" as far as the animation layer is concerned, so it is
    // a detachable group with the belt run parented into it. Dropping it on a reload
    // therefore takes the belt with it, which is exactly right.
    const box = [];
    box.push(place(chamferBox(0.086, 0.104, 0.146, 0.0042), 0, -0.052, 0));
    box.push(place(chamferBox(0.092, 0.014, 0.152, 0.0032), 0, 0.004, 0)); // lid
    box.push(place(chamferBox(0.016, 0.022, 0.012, 0.0022), 0, -0.006, 0.078)); // latch
    box.push(place(chamferBox(0.070, 0.012, 0.014, 0.0022), 0, -0.008, -0.076)); // hinge
    // Stiffening ribs down both flanks.
    for (let i = -1; i <= 1; i++) {
      box.push(place(chamferBox(0.090, 0.010, 0.010, 0.0018), 0, -0.052, i * 0.040));
    }
    const boxGeom = mergeGeom(box);

    const grp = new THREE.Group();
    const bm = new THREE.Mesh(boxGeom, M.poly);
    bm.frustumCulled = false;
    grp.add(bm);
    // Belt: out of the box mouth, up the left flank, into the feed chute.
    const belt = new THREE.Mesh(ammoBelt(6, [-0.030, 0.010, -0.020], [-0.026, 0.070, -0.052], 0.010), M.dark);
    belt.frustumCulled = false;
    grp.add(belt);
    grp.position.set(0, -0.086, -0.048);
    parts.magazine = grp;
    parts.magGeom = boxGeom;
    parts.magMat = M.poly;
  }
  // The short run of belt still standing in the feed tray once the box is gone.
  metal.push(translate(ammoBelt(2, [-0.022, -0.018, -0.096], [-0.008, -0.004, -0.100], 0.002), 0, 0, 0));

  /* -------------------------------- handguard ------------------------------- */
  metal.push(translate(tubeZ([[0.0290, -0.206], [0.0290, -0.182], [0.026, -0.180]], 14), 0, 0, 0)); // trunnion
  {
    const hgLen = 0.150;
    const hgZ = -0.284;
    const side = slottedPanel(0.040, 0.0060, hgLen, 4, 0.44);
    const left = rotate(side.clone(), 0, 0, Math.PI / 2);
    translate(left, -0.0232, -0.002, hgZ);
    metal.push(left);
    const right = rotate(side, 0, 0, Math.PI / 2);
    translate(right, 0.0232, -0.002, hgZ);
    metal.push(right);
    const belly = slottedPanel(0.034, 0.0060, hgLen, 4, 0.44);
    translate(belly, 0, -0.0245, hgZ);
    metal.push(belly);
    for (const [sx, sy] of [[1, 1], [-1, 1], [1, -1], [-1, -1]]) {
      const f = chamferBox(0.020, 0.006, hgLen, 0.0010);
      rotate(f, 0, 0, (sx * sy > 0 ? -1 : 1) * Math.PI * 0.25);
      translate(f, sx * 0.0182, sy * 0.0182 - 0.002, hgZ);
      metal.push(f);
    }
    metal.push(translate(picatinnyRail(0.070, 0.0180, -0.246), 0, -0.0335, 0));
  }
  // Angled foregrip: where the support hand actually goes when the bipod is folded.
  {
    const g = [];
    g.push(place(chamferBox(0.028, 0.066, 0.032, 0.0048), 0, -0.030, 0));
    g.push(place(chamferBox(0.032, 0.010, 0.038, 0.0030), 0, 0.002, 0));
    g.push(translate(gripTexture(0.020, 0.040, 3, 3, 0.0034, 0.0009), 0, -0.034, 0.0162));
    const geo = mergeGeom(g);
    rotate(geo, 0.58, 0, 0);
    translate(geo, 0, -0.036, -0.276);
    poly.push(geo);
  }

  /* ------------------------------ heavy barrel ------------------------------ */
  metal.push(translate(rodZ(0.0150, -0.400, -0.196, 14), 0, 0, 0));
  metal.push(translate(rodZ(0.0132, -0.560, -0.400, 12), 0, 0, 0));
  // Cooling flutes down the chamber end: the mass that lets a barrel eat a belt.
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const f = plainBox(0.006, 0.006, 0.150);
    translate(f, Math.cos(a) * 0.0150, Math.sin(a) * 0.0150, -0.300);
    metal.push(f);
  }
  // Gas block and tube. Top of the block sits at 0.021; the lowest sight ray passes
  // 0.042 at this z, so it stays clear of the sight picture.
  metal.push(place(chamferBox(0.030, 0.032, 0.036, 0.0018), 0, 0.003, -0.352));
  metal.push(translate(rodZ(0.0040, -0.350, -0.196, 8), 0, 0.0165, 0));
  metal.push(place(chamferBox(0.014, 0.020, 0.014, 0.0012), 0.020, 0.004, -0.352)); // gas regulator
  /**
   * Carry handle. Barrel changes are the reason it exists, so it is on the *barrel*,
   * forward of the gas block, and not on the receiver.
   *
   * Its top is the tightest clearance on this weapon, and the first version got it
   * wrong: at a 39 mm top it stopped every ray below 3.7 degrees, which put a tan bar
   * across the bottom of the sight picture — measured by raycasting the sight line,
   * and plainly visible in the screenshot. The tube's own clear cone is 4.83 degrees,
   * which is 32 mm at the front upright, so the whole assembly now tops out at 30.
   * A carry handle that blocks the sight is the same bug as a knuckle that does.
   */
  metal.push(place(chamferBox(0.014, 0.018, 0.016, 0.0016), 0, 0.0205, -0.268));
  metal.push(place(chamferBox(0.014, 0.018, 0.016, 0.0016), 0, 0.0205, -0.332));
  metal.push(place(chamferBox(0.020, 0.011, 0.086, 0.0022), 0, 0.0235, -0.300));
  poly.push(place(chamferBox(0.024, 0.013, 0.062, 0.0028), 0, 0.0235, -0.300)); // rubber sleeve
  // Muzzle: a slotted flash hider on a stepped crown.
  metal.push(translate(rodZ(0.0142, -0.586, -0.560, 12), 0, 0, 0));
  metal.push(pipeZ(0.0170, 0.0110, -0.640, -0.586, 12));
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + Math.PI / 6;
    const p = plainBox(0.006, 0.006, 0.030);
    translate(p, Math.cos(a) * 0.0146, Math.sin(a) * 0.0146, -0.616);
    metal.push(p);
  }

  /* --------------------------------- bipod ---------------------------------- */
  {
    const b = [];
    b.push(place(chamferBox(0.030, 0.026, 0.040, 0.0020), 0, -0.028, -0.470)); // yoke
    b.push(translate(rodX(0.0048, -0.020, 0.020, 8), 0, -0.038, -0.470)); // pivot
    for (const sx of [-1, 1]) {
      // Legs rake down and forward, splayed outward — a folded bipod would be
      // invisible under the handguard, and this weapon's whole pitch is bracing.
      const leg = chamferBox(0.011, 0.011, 0.185, 0.0014);
      rotate(leg, 0.34, 0, sx * 0.30);
      translate(leg, sx * 0.036, -0.115, -0.500);
      b.push(leg);
      const foot = chamferBox(0.016, 0.010, 0.030, 0.0018);
      rotate(foot, 0.34, 0, 0);
      translate(foot, sx * 0.062, -0.198, -0.528);
      b.push(foot);
      // Leg spring collar.
      const collar = chamferBox(0.014, 0.014, 0.014, 0.0012);
      translate(collar, sx * 0.020, -0.048, -0.478);
      b.push(collar);
    }
    metal.push(mergeGeom(b));
  }

  /* ---------------------------------- optic --------------------------------- */
  {
    const o = [];
    o.push(place(chamferBox(0.030, 0.016, 0.056, 0.0018), 0, 0.0475, -0.032)); // mount body
    o.push(place(chamferBox(0.034, 0.012, 0.014, 0.0016), 0, 0.0430, -0.056)); // clamp
    o.push(place(chamferBox(0.010, 0.012, 0.012, 0.0012), -0.020, 0.0430, -0.056)); // throw lever
    metal.push(mergeGeom(o));

    // A wide-tube reflex: 31 mm of clear aperture against the rifle's 30, and a much
    // shorter tube, which is what opens the clear cone to 4.8 degrees — the widest
    // sight picture in the file. On a suppression weapon the field around the dot
    // matters more than the dot.
    const body = [];
    body.push(pipeZ(0.0192, 0.0155, -0.062, -0.004, 14));
    body.push(translate(tubeZ([[0.0155, -0.062], [0.0212, -0.062], [0.0212, -0.053], [0.0192, -0.052]], 16), 0, 0, 0));
    body.push(translate(tubeZ([[0.0192, -0.014], [0.0212, -0.013], [0.0212, -0.004], [0.0155, -0.004]], 16), 0, 0, 0));
    body.push(place(chamferBox(0.011, 0.013, 0.013, 0.0012), 0, 0.021, -0.033)); // elevation turret
    body.push(place(chamferBox(0.013, 0.011, 0.013, 0.0012), -0.021, 0.0, -0.033)); // windage turret
    body.push(place(chamferBox(0.015, 0.008, 0.026, 0.0010), 0.019, 0.006, -0.026)); // battery boss
    const bg = mergeGeom(body);
    translate(bg, 0, SIGHT_Y, 0);
    metal.push(bg);

    // Matte-black bore sleeve, held 0.5 mm inside the tube wall for the same reason
    // the rifle's is: at 0.1 mm the two surfaces z-fight and speckle the inside of
    // the tube with lit gunmetal.
    parts.bore = boreSleeve(M, 0.0150, -0.0610, -0.0050, SIGHT_Y, 18);

    const lensFront = new THREE.Mesh(discZ(0.0155, -0.0605, 16, -1), M.lens);
    lensFront.position.y = SIGHT_Y;
    lensFront.frustumCulled = false;
    lensFront.renderOrder = 20;
    parts.lensFront = lensFront;
    const lensRear = new THREE.Mesh(discZ(0.0155, -0.0055, 16, 1), M.lens);
    lensRear.position.y = SIGHT_Y;
    lensRear.frustumCulled = false;
    lensRear.renderOrder = 21;
    parts.lensRear = lensRear;

    // Reticle: a dot inside a segmented ring. The ring is the LMG's own tell — it is
    // the reticle you can still find after twenty rounds have walked the muzzle off
    // the target, which a bare dot is not.
    const ret = new THREE.Group();
    const dot = new THREE.Mesh(new THREE.CircleGeometry(0.0016, 10), M.dot);
    ret.add(dot);
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const seg = new THREE.Mesh(new THREE.PlaneGeometry(0.0030, 0.0011), M.dot);
      seg.position.set(Math.cos(a) * 0.0072, Math.sin(a) * 0.0072, 0);
      seg.rotation.z = a + Math.PI / 2;
      ret.add(seg);
    }
    ret.position.set(0, SIGHT_Y, -0.032);
    ret.renderOrder = 60;
    ret.traverse((o2) => {
      if (o2.isMesh) {
        o2.material.depthTest = false;
        o2.material.depthWrite = false;
        o2.frustumCulled = false;
      }
    });
    parts.reticle = ret;
  }

  /* ------------------------------ animated parts ---------------------------- */
  {
    const b = [];
    b.push(place(chamferBox(0.032, 0.028, 0.104, 0.0015), 0.003, -0.002, 0.010));
    b.push(place(chamferBox(0.016, 0.018, 0.024, 0.0012), 0.003, -0.002, -0.046));
    const bolt = new THREE.Mesh(mergeGeom(b), M.dark);
    bolt.frustumCulled = false;
    parts.bolt = bolt;
  }
  {
    // Charging handle on the right, on its own rail slot.
    const c = [];
    c.push(place(chamferBox(0.016, 0.020, 0.042, 0.0014), 0.030, 0.006, 0.010));
    c.push(place(chamferBox(0.024, 0.012, 0.014, 0.0012), 0.038, 0.006, 0.026));
    const ch = new THREE.Mesh(mergeGeom(c), M.metal);
    ch.frustumCulled = false;
    parts.charging = ch;
  }
  {
    const t = new THREE.Mesh(mergeGeom([place(chamferBox(0.007, 0.028, 0.010, 0.0012), 0, -0.012, 0)]), M.dark);
    t.position.set(0, -0.066, 0.050);
    t.frustumCulled = false;
    parts.trigger = t;
  }

  const built = assemble(ctx, { metal: { geoms: metal, mat: M.metal }, poly: { geoms: poly, mat: M.poly } });
  const root = built.root;
  for (const k of ['bolt', 'charging', 'trigger', 'magazine', 'bore', 'lensFront', 'lensRear', 'reticle']) {
    if (parts[k]) root.add(parts[k]);
  }

  const hands = attachHands(ctx, root, parts, {
    right: { anchor: [0.030, -0.086, 0.104], curl: 0.98, trigger: 0.74 },
    // On the angled foregrip, well under the bore — the support hand is the reason
    // every sight in this file was blocked before, and it stays out of the cone here.
    left: { anchor: [-0.038, -0.030, -0.278], curl: 0.96, spread: 0.32 },
  });

  return {
    root,
    parts,
    hands,
    reticleCollimated: true,
    reticleClear: 0.0132,
    aimPoint: new THREE.Vector3(0, SIGHT_Y, -0.032),
    muzzlePoint: new THREE.Vector3(0, 0, -0.640),
    ejectPoint: new THREE.Vector3(0.028, -0.004, -0.028),
    ejectDir: new THREE.Vector3(0.88, 0.30, 0.36),
    magOffset: new THREE.Vector3(0, -0.086, -0.048),
  };
}

/* -------------------------------------------------------------------------- */
/* SV-338 — bolt-action sniper rifle                                           */
/* -------------------------------------------------------------------------- */

function buildSniper(ctx) {
  const M = materials(ctx);
  const metal = [];
  const poly = [];
  const parts = {};

  const SIGHT_Y = 0.0760;

  /* -------------------------------- receiver -------------------------------- */
  // Flat-sided and square: a bolt action has no reciprocating carrier to house, so
  // the receiver is a machined billet rather than the rifle's pressed shell.
  metal.push(place(chamferBox(0.044, 0.054, 0.310, 0.0018), 0, 0.000, -0.020));
  metal.push(place(chamferBox(0.030, 0.008, 0.300, 0.0011), 0, 0.028, -0.020));
  // One continuous 20-MOA rail: a scope this size needs the whole top of the action.
  // 20 teeth over 300 mm rather than the default 29. A rail this long is read as a
  // texture, not counted, and the triangles belong in the scope.
  metal.push(translate(picatinnyRail(0.300, 0.0212, 0.120, 20), 0, 0.0280, 0));
  // Ejection/loading port on the right, with the bolt raceway behind it.
  metal.push(place(chamferBox(0.005, 0.030, 0.086, 0.0011), 0.0218, 0.004, -0.020));
  metal.push(place(chamferBox(0.006, 0.020, 0.120, 0.0011), 0.0218, 0.014, 0.080)); // bolt raceway
  metal.push(place(chamferBox(0.040, 0.020, 0.020, 0.0016), 0, 0.014, 0.128)); // shroud boss
  metal.push(translate(rodX(0.0044, -0.024, 0.024, 8), 0, -0.024, -0.108)); // recoil lug pin
  metal.push(translate(rodX(0.0044, -0.024, 0.024, 8), 0, -0.024, 0.062)); // rear action screw

  /* ------------------------------ chassis / stock --------------------------- */
  metal.push(place(chamferBox(0.040, 0.046, 0.150, 0.0022), 0, -0.048, 0.040)); // chassis block
  metal.push(place(chamferBox(0.044, 0.070, 0.056, 0.0026), 0, -0.050, -0.048)); // magwell
  metal.push(place(chamferBox(0.048, 0.010, 0.062, 0.0028), 0, -0.086, -0.048)); // flared lip
  metal.push(translate(rodX(0.0056, 0.020, 0.027, 8), 0, -0.036, -0.006)); // mag release
  metal.push(translate(triggerGuard(0.004, 0.070, -0.068, -0.102, 0.0082), 0, 0, 0));

  {
    // Thumbhole grip: vertical, with a shelf for the thumb over the tang. It is the
    // shape that says "chassis rifle" rather than "hunting stock".
    const g = [];
    g.push(place(chamferBox(0.034, 0.116, 0.048, 0.0060), 0, -0.057, 0));
    g.push(place(chamferBox(0.036, 0.014, 0.038, 0.0035), 0, -0.117, 0.002));
    g.push(place(chamferBox(0.038, 0.024, 0.016, 0.0032), 0, -0.008, -0.016)); // thumb shelf
    for (let i = 0; i < 3; i++) {
      g.push(place(chamferBox(0.032, 0.012, 0.008, 0.0025), 0, -0.034 - i * 0.028, -0.024));
    }
    g.push(translate(gripTexture(0.026, 0.078, 3, 6, 0.0040, 0.0011), 0, -0.060, 0.0242));
    const geo = mergeGeom(g);
    // Steeper than the rifle's: a precision grip is nearly vertical because the hand
    // is only steering the trigger, not holding the weapon up.
    rotate(geo, -0.26, 0, 0);
    translate(geo, 0, -0.070, 0.086);
    poly.push(geo);
  }

  {
    // Skeletonised buttstock: two struts and a comb rather than a solid block, with
    // the lightening cut between them left genuinely open.
    const s = [];
    s.push(place(chamferBox(0.036, 0.026, 0.190, 0.0030), 0, 0.014, 0.216)); // top strut
    s.push(place(chamferBox(0.032, 0.022, 0.170, 0.0030), 0, -0.062, 0.208)); // bottom strut
    s.push(place(chamferBox(0.040, 0.070, 0.026, 0.0035), 0, -0.024, 0.140)); // front web
    s.push(place(chamferBox(0.048, 0.104, 0.022, 0.0045), 0, -0.022, 0.302)); // butt pad
    s.push(place(chamferBox(0.050, 0.016, 0.016, 0.0030), 0, -0.070, 0.292)); // adjustable toe
    poly.push(mergeGeom(s));
    // Adjustable cheek riser on two posts — the part a sniper actually adjusts.
    const c = [];
    c.push(place(chamferBox(0.042, 0.024, 0.120, 0.0035), 0, 0.044, 0.212));
    c.push(place(chamferBox(0.010, 0.028, 0.010, 0.0014), -0.014, 0.030, 0.176));
    c.push(place(chamferBox(0.010, 0.028, 0.010, 0.0014), 0.014, 0.030, 0.176));
    poly.push(mergeGeom(c));
    metal.push(place(chamferBox(0.012, 0.014, 0.012, 0.0012), 0.024, 0.030, 0.176)); // riser thumbwheel
  }
  metal.push(place(ringZ(0.0064, 0.0023, 12, 6), -0.0215, -0.062, 0.240, 0, Math.PI / 2, 0));

  /* -------------------------------- handguard ------------------------------- */
  metal.push(translate(tubeZ([[0.0270, -0.196], [0.0270, -0.176], [0.024, -0.174]], 14), 0, 0, 0));
  {
    const hgLen = 0.190;
    const hgZ = -0.296;
    const side = slottedPanel(0.038, 0.006, hgLen, 5, 0.46);
    const left = rotate(side.clone(), 0, 0, Math.PI / 2);
    translate(left, -0.0224, -0.002, hgZ);
    metal.push(left);
    const right = rotate(side, 0, 0, Math.PI / 2);
    translate(right, 0.0224, -0.002, hgZ);
    metal.push(right);
    const belly = slottedPanel(0.032, 0.006, hgLen, 5, 0.46);
    translate(belly, 0, -0.0230, hgZ);
    metal.push(belly);
    for (const [sx, sy] of [[1, 1], [-1, 1], [1, -1], [-1, -1]]) {
      const f = chamferBox(0.019, 0.006, hgLen, 0.0010);
      rotate(f, 0, 0, (sx * sy > 0 ? -1 : 1) * Math.PI * 0.25);
      translate(f, sx * 0.0176, sy * 0.0176 - 0.002, hgZ);
      metal.push(f);
    }
    metal.push(translate(picatinnyRail(0.086, 0.0180, -0.320), 0, -0.0320, 0));
  }

  /* ------------------------------ match barrel ------------------------------ */
  metal.push(translate(rodZ(0.0148, -0.520, -0.186, 14), 0, 0, 0));
  // Eight flutes, deeper and longer than the DMR's six: this is the heavier barrel.
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const f = plainBox(0.0060, 0.0060, 0.150);
    translate(f, Math.cos(a) * 0.0148, Math.sin(a) * 0.0148, -0.430);
    metal.push(f);
  }
  metal.push(translate(rodZ(0.0126, -0.560, -0.520, 12), 0, 0, 0));
  /**
   * Muzzle brake. Its outer radius is 17 mm and the lowest ray of the sight picture
   * passes 20.9 mm at this z, so it sits just under the bottom edge of the scope's
   * clear cone. Any longer a barrel — or any fatter a brake — and the front of this
   * weapon would be visible inside its own sight picture.
   */
  metal.push(pipeZ(0.0170, 0.0104, -0.628, -0.560, 14));
  for (let i = 0; i < 3; i++) {
    for (const sx of [-1, 1]) {
      const p = plainBox(0.007, 0.020, 0.011);
      translate(p, sx * 0.0140, 0, -0.574 - i * 0.019);
      metal.push(p);
    }
  }

  /* --------------------------------- bipod ---------------------------------- */
  {
    const b = [];
    b.push(place(chamferBox(0.032, 0.024, 0.044, 0.0020), 0, -0.030, -0.372));
    b.push(translate(rodX(0.0050, -0.022, 0.022, 8), 0, -0.040, -0.372));
    for (const sx of [-1, 1]) {
      const leg = chamferBox(0.012, 0.012, 0.200, 0.0014);
      rotate(leg, 0.30, 0, sx * 0.26);
      translate(leg, sx * 0.034, -0.126, -0.402);
      b.push(leg);
      const foot = chamferBox(0.018, 0.011, 0.032, 0.0018);
      rotate(foot, 0.30, 0, 0);
      translate(foot, sx * 0.058, -0.218, -0.432);
      b.push(foot);
      const collar = chamferBox(0.015, 0.015, 0.015, 0.0012);
      translate(collar, sx * 0.019, -0.050, -0.380);
      b.push(collar);
    }
    metal.push(mergeGeom(b));
  }

  /* ---------------------------------- scope --------------------------------- */
  {
    const o = [];
    // 34 mm rings, tall enough to clear the objective bell over the rail.
    o.push(place(chamferBox(0.034, 0.022, 0.030, 0.0018), 0, 0.0450, -0.062));
    o.push(place(chamferBox(0.034, 0.022, 0.030, 0.0018), 0, 0.0450, 0.038));
    o.push(place(chamferBox(0.038, 0.010, 0.034, 0.0016), 0, 0.0360, -0.062)); // ring feet
    o.push(place(chamferBox(0.038, 0.010, 0.034, 0.0016), 0, 0.0360, 0.038));
    metal.push(mergeGeom(o));

    const body = [];
    /**
     * Outer surface, objective to ocular, as one profile.
     *
     * The number that decides whether a scope can be aimed through is the clear cone
     * `atan(r / d)` at its tightest internal shoulder — here the step from the 60 mm
     * objective bell down to the main tube. The first version used a 42 mm tube with
     * the shoulder 334 mm from the eye: 3.63 degrees, no better than the marksman
     * rifle's 3.51 despite nearly twice the magnification, and measured on screen it
     * was a 7-degree keyhole inside a 19-degree ocular. A 48 mm tube with the
     * shoulder pulled back to 324 mm opens it to 4.15 — 31% more sight picture by
     * area — and the extra 3 mm of wall is invisible next to a 60 mm objective bell.
     * Real high-power glass gets wider, not thinner, for exactly this reason.
     */
    body.push(
      tubeZ(
        [
          [0.0272, -0.1520],
          [0.0300, -0.1470],
          [0.0300, -0.1010],
          [0.0238, -0.0750],
          [0.0238, 0.0400],
          [0.0250, 0.0520],
          [0.0250, 0.0980],
          [0.0244, 0.1010],
        ],
        18
      )
    );
    // Inner wall of the ocular bell only. The objective end gets none: 25 cm down the
    // sight line an inward-facing ring draws a bright halo across the middle of the
    // picture, and the bell's own outer cone is back-facing from inside the tube.
    body.push(tubeZ([[0.0244, 0.1010], [0.0244, 0.0985]], 18));
    // Turrets: tall target knobs, and both start *outside* the tube wall (0.0212) so
    // their base discs cannot cap the bore. Elevation is vertical, windage lateral.
    {
      const t = tubeZ([[0.0, 0.0], [0.0150, 0.0], [0.0150, 0.0230], [0.0132, 0.0246], [0.0, 0.0246]], 12);
      t.rotateX(-Math.PI / 2);
      t.translate(0, 0.0250, -0.030);
      body.push(t);
    }
    {
      // Mirrored (`rotateY(-PI/2)`) so it grows outboard. The other sign runs the
      // turret body straight through the middle of the bore — the exact bug the
      // marksman rifle's windage turret had.
      const t = tubeZ([[0.0, 0.0], [0.0138, 0.0], [0.0138, 0.0200], [0.0120, 0.0216], [0.0, 0.0216]], 12);
      t.rotateY(-Math.PI / 2);
      t.translate(-0.0250, 0, -0.030);
      body.push(t);
    }
    // Parallax knob on the left, forward of the saddle.
    {
      const t = tubeZ([[0.0, 0.0], [0.0120, 0.0], [0.0120, 0.0150], [0.0104, 0.0164], [0.0, 0.0164]], 12);
      t.rotateY(-Math.PI / 2);
      t.translate(-0.0250, 0, -0.062);
      body.push(t);
    }
    // Turret saddle: a collar *around* the tube, built from an outer wall and two end
    // faces. A `pipeZ` would carry an inward-facing inner wall, and anything
    // inward-facing inside this tube is drawn straight into the sight picture.
    body.push(tubeZ([[0.0298, -0.0400], [0.0298, -0.0210]], 18));
    body.push(tubeZ([[0.0240, -0.0400], [0.0298, -0.0400]], 18));
    body.push(tubeZ([[0.0298, -0.0210], [0.0240, -0.0210]], 18));
    for (const [dx, dy] of [[0, 0.0294], [-0.0294, 0]]) {
      body.push(place(chamferBox(dx ? 0.011 : 0.028, dy ? 0.011 : 0.028, 0.020, 0.0012), dx, dy, -0.030));
    }
    // Magnification ring with a throw lever, and a knurled band behind it.
    body.push(tubeZ([[0.0280, 0.0180], [0.0280, 0.0380]], 18));
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      body.push(translate(plainBox(0.0045, 0.0045, 0.020), Math.cos(a) * 0.0280, Math.sin(a) * 0.0280, 0.028));
    }
    body.push(place(chamferBox(0.012, 0.030, 0.014, 0.0014), -0.0276, 0.014, 0.028)); // throw lever
    const bg = mergeGeom(body);
    translate(bg, 0, SIGHT_Y, 0);
    metal.push(bg);

    /**
     * Bore liner, and the one place this scope deliberately departs from the marksman
     * rifle's build.
     *
     * The SR-12 lines only its ocular bell, on the correct principle that a sleeve
     * moves the limiting rim to its own far end and can turn a scope into a drinking
     * straw. But leaving the rest bare has a cost that shows up the moment you aim at
     * the sky: the tube wall is a single-sided surface of revolution, so from *inside*
     * the bore it is invisible, and the eye looks straight through it at whatever is
     * bolted to the outside — the saddle's end faces, the turret bases, the magnifier
     * ring. Photographed against bright sky, the marksman rifle's sight picture is a
     * ring of lit machined parts with a small disc of world in the middle.
     *
     * The fix is not to shorten the liner but to place its far rim exactly where the
     * limiting aperture already is. This one runs from the ocular down to the
     * objective shoulder at r = 23.0 mm, 0.8 mm inside the tube wall (0.1 mm z-fights;
     * 0.5 mm was measured clean on the rifle). Its rim sits within a hair of the
     * shoulder that was already the limit, so the clear cone goes from 4.15 to 4.07
     * degrees — a 2% cost — and in exchange everything outside the cone is flat black
     * instead of sunlit gunmetal, which is what a real blackened tube delivers.
     */
    parts.bore = boreSleeve(M, 0.0230, -0.0745, 0.1000, SIGHT_Y, 20);

    // The objective shoulder, blacked out 0.3 mm inside the wall. It is the one
    // inward-facing surface visible all the way down the bore, and in gunmetal it
    // renders as a lit grey plate across most of the sight picture.
    {
      const s = new THREE.Mesh(
        tubeZ([[0.0297, -0.1460], [0.0297, -0.1015], [0.0235, -0.0755]], 18),
        M.bore
      );
      s.position.y = SIGHT_Y;
      s.frustumCulled = false;
      s.renderOrder = 8;
      parts.boreFront = s;
    }

    const objective = new THREE.Mesh(discZ(0.0272, -0.1505, 20, -1), M.lens);
    objective.position.y = SIGHT_Y;
    objective.frustumCulled = false;
    objective.renderOrder = 20;
    parts.lensFront = objective;
    const ocular = new THREE.Mesh(discZ(0.0244, 0.1005, 18, 1), M.lens);
    ocular.position.y = SIGHT_Y;
    ocular.frustumCulled = false;
    ocular.renderOrder = 21;
    parts.lensRear = ocular;

    // Scope shadow: the black ring that creeps in from the edge of a magnified
    // ocular. At 5.7x it is the single loudest cue that this is not the DMR.
    {
      const sh = new THREE.Mesh(
        new THREE.CircleGeometry(0.0252, 36),
        new THREE.MeshBasicMaterial({
          map: vignetteTexture(),
          color: 0x000000,
          transparent: true,
          depthTest: false,
          depthWrite: false,
          toneMapped: false,
        })
      );
      sh.position.set(0, SIGHT_Y, 0.1002);
      sh.renderOrder = 40;
      sh.frustumCulled = false;
      parts.scopeShadow = sh;
    }

    /**
     * Reticle: a fine-line mil-dot with a floating centre.
     *
     * The lines stop short of the middle rather than crossing it. At this
     * magnification a solid intersection covers most of a torso at 200 m, and a
     * reticle that hides the thing you are aiming at is the one mistake a sniper
     * scope cannot make.
     */
    const ret = new THREE.Group();
    const rmat = new THREE.MeshBasicMaterial({ color: 0x08080a, toneMapped: false });
    const mk = (w, h, x, y) => {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), rmat);
      m.position.set(x, y, 0);
      return m;
    };
    for (const s of [-1, 1]) {
      ret.add(mk(0.0006, 0.0170, 0, s * 0.0110));
      ret.add(mk(0.0170, 0.0006, s * 0.0110, 0));
    }
    ret.add(mk(0.0010, 0.0010, 0, 0)); // centre pip
    for (const d of [-0.0140, -0.0090, -0.0045, 0.0045, 0.0090, 0.0140]) {
      ret.add(mk(0.0016, 0.0016, d, 0));
      ret.add(mk(0.0016, 0.0016, 0, d));
    }
    // Holdover ladder below centre: the mark of a ranging reticle.
    for (const [d, w] of [[-0.0068, 0.0060], [-0.0114, 0.0044], [-0.0160, 0.0030]]) {
      ret.add(mk(w, 0.0006, 0, d));
    }
    ret.position.set(0, SIGHT_Y, 0.0940);
    ret.renderOrder = 60;
    ret.traverse((o2) => {
      if (o2.isMesh) {
        o2.material.depthTest = false;
        o2.material.depthWrite = false;
        o2.frustumCulled = false;
      }
    });
    parts.reticle = ret;
  }

  /* ------------------------------ animated parts ---------------------------- */
  {
    /**
     * The bolt, as a group whose pivot is the bore axis, so `rotation.z` lifts the
     * handle and `position.z` runs it back — which is the whole cycle. Nothing else
     * in the file needs a rotating part; a bolt gun is defined by one.
     */
    const g = new THREE.Group();
    const body = new THREE.Mesh(
      mergeGeom([
        translate(rodZ(0.0104, -0.020, 0.150, 12), 0, 0, 0),
        place(chamferBox(0.024, 0.024, 0.020, 0.0016), 0, 0, 0.150), // shroud
        place(chamferBox(0.014, 0.014, 0.012, 0.0012), 0, 0, 0.166), // cocking indicator
      ]),
      M.dark
    );
    body.position.y = 0.012;
    g.add(body);
    const handle = new THREE.Mesh(
      mergeGeom([
        place(chamferBox(0.042, 0.011, 0.012, 0.0014), 0.028, 0, 0.086),
        place(chamferBox(0.014, 0.014, 0.014, 0.0022), 0.050, 0, 0.086),
      ]),
      M.metal
    );
    handle.position.y = 0.012;
    g.add(handle);
    g.traverse((o2) => (o2.frustumCulled = false));
    parts.bolt = g;
    parts.boltIsGroup = true;
  }
  {
    const t = new THREE.Mesh(mergeGeom([place(chamferBox(0.007, 0.030, 0.010, 0.0012), 0, -0.013, 0)]), M.dark);
    t.position.set(0, -0.070, 0.046);
    t.frustumCulled = false;
    parts.trigger = t;
  }
  {
    // Five rounds of .338 in a detachable box: short, straight and heavy-walled.
    const mag = curvedMagazine(0.0288, 0.0430, 0.038, 3, 0.030, 0.0026);
    const mg = new THREE.Group();
    const mm = new THREE.Mesh(mag.geom, M.metal);
    mm.frustumCulled = false;
    mg.add(mm);
    mg.position.set(0, -0.082, -0.048);
    parts.magazine = mg;
    parts.magGeom = mag.geom;
    parts.magMat = M.metal;
  }

  const built = assemble(ctx, { metal: { geoms: metal, mat: M.metal }, poly: { geoms: poly, mat: M.poly } });
  const root = built.root;
  for (const k of ['bolt', 'trigger', 'magazine', 'bore', 'boreFront', 'lensFront', 'lensRear', 'scopeShadow', 'reticle']) {
    if (parts[k]) root.add(parts[k]);
  }

  const hands = attachHands(ctx, root, parts, {
    right: { anchor: [0.030, -0.090, 0.106], curl: 0.98, trigger: 0.68 },
    left: { anchor: [-0.038, -0.026, -0.296], curl: 0.95, spread: 0.34 },
  });

  return {
    root,
    parts,
    hands,
    reticleCollimated: false, // an etched reticle sits in the tube, not at infinity
    aimPoint: new THREE.Vector3(0, SIGHT_Y, 0.074),
    muzzlePoint: new THREE.Vector3(0, 0, -0.630),
    ejectPoint: new THREE.Vector3(0.026, 0.010, -0.014),
    ejectDir: new THREE.Vector3(0.90, 0.34, 0.26),
    magOffset: new THREE.Vector3(0, -0.082, -0.048),
  };
}

/* -------------------------------------------------------------------------- */
/* hands                                                                       */
/* -------------------------------------------------------------------------- */

// `GRIPS`, `orientHand` and `attachHands` moved to `hands.js` when the chassis was
// added: the chassis needs them and importing them back out of this module would be
// circular. The support-hand pose comment — the one that explains why a knuckle row
// at bore height blocked every sight in the game — moved with them.

/* -------------------------------------------------------------------------- */

/**
 * The roster.
 *
 * Twenty-three weapons built from specs through the shared chassis, plus the six
 * original archetypes kept as fallbacks. `WeaponSystem` probes each `model` id and
 * falls back to the class archetype if a builder throws, so a half-finished weapon
 * degrades to a working one rather than taking the weapon layer down — which is what
 * let the roster be landed one weapon at a time against a running game.
 */
const BUILDERS = {
  ar: buildAR,
  smg: buildSMG,
  dmr: buildDMR,
  shotgun: buildShotgun,
  lmg: buildLMG,
  sniper: buildSniper,
};

for (const [id, spec] of Object.entries(SPECS)) {
  BUILDERS[id] = (ctx) => buildFirearm(ctx, spec);
}

/** Eye relief per weapon, read by the viewmodel. Lives with the spec that solves it. */
export const ADS_BY_MODEL = Object.fromEntries(Object.entries(SPECS).map(([id, s]) => [id, s.ads]));

/** Which archetype a weapon falls back to if its builder throws. */
export const MODEL_CLASS = Object.fromEntries(Object.entries(SPECS).map(([id, s]) => [id, s.cls]));

/**
 * Build a weapon model. The result's `root` is in model space (metres); the
 * viewmodel is responsible for scaling and positioning it in view space.
 */
export function buildWeaponModel(id, ctx) {
  const fn = BUILDERS[id];
  if (!fn) throw new Error(`[weapons] no model builder for "${id}"`);
  const m = fn(ctx);
  m.root.traverse((o) => {
    o.frustumCulled = false;
    if (o.isMesh) {
      o.castShadow = false;
      o.receiveShadow = false;
    }
  });
  m.triangles = triCount(m.root);
  m.id = id;
  return m;
}

export { triCount };
